/**
 * Matching Page
 * Match bank payments to scheduled amortization payments
 * Also manages receipts for capital received from investors
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as MatchIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  LinkOff as UnmatchIcon,
  Edit as EditIcon,
  Visibility as PreviewIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  Merge as MergeIcon,
  SwapVert as SwapVertIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useForm, Controller } from 'react-hook-form';
import { apiGet, apiPost, apiPut, apiUpload, apiDelete } from '../utils/apiClient';
import { formatNumber } from '../utils/validation';
import { investorLabel, investorSearchText, toTableRow } from '../utils/paymentHelpers';
import TabPanel from '../components/common/TabPanel';
import ConfirmDialog from '../components/common/ConfirmDialog';
import FileDropZone from '../components/common/FileDropZone';
import { OverduePaymentsTab, UpcomingPaymentsTab } from '../components/payments';
import MatchDialog from '../components/payments/MatchDialog';
import PaymentProofCell from '../components/payments/PaymentProofCell';
import StatCard from '../components/common/StatCard';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import useDocumentActions from '../hooks/useDocumentActions';
import DocumentPreviewDialog from '../components/documents/DocumentPreviewDialog';
import ContractReferenceCell from '../components/contracts/ContractReferenceCell';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import useTableSort from '../hooks/useTableSort';
import usePagination from '../hooks/usePagination';
import logger from '../utils/logger';

const MATCHED_PAYMENT_COLUMNS = [
  { key: 'contract',        align: 'left',  getValue: (p) => p.contract_reference },
  { key: 'investor',        align: 'left',  getValue: (p) => investorLabel(p) },
  { key: 'payment_date',    align: 'left',  getValue: (p) => p.payment_date },
  { key: 'amount',          align: 'right', getValue: (p) => p.payment_amount != null ? parseFloat(p.payment_amount) : null },
  { key: 'matched_date',    align: 'left',  getValue: (p) => p.processed_at },
  { key: 'matched_by',      align: 'left',  getValue: (p) => p.processed_by || p.created_by },
  { key: 'reference',       align: 'left',  getValue: (p) => p.payment_reference },
  { key: 'status',          align: 'left',  getValue: (p) => p.payment_status },
];

const PENDING_DRAWDOWN_COLUMNS = [
  { key: 'contract',       align: 'left',  getValue: (d) => d.contract_reference },
  { key: 'investor',       align: 'left',  getValue: (d) => investorLabel(d) },
  { key: 'expected_date',  align: 'left',  getValue: (d) => d.expected_date },
  { key: 'capital_amount', align: 'right', getValue: (d) => d.capital_amount != null ? parseFloat(d.capital_amount) : null },
  { key: 'days_overdue',   align: 'left',  getValue: (d) => d.days_overdue != null ? Number(d.days_overdue) : 0 },
];

const RECEIPT_COLUMNS = [
  { key: 'contract',     align: 'left',  getValue: (r) => r.contract_reference },
  { key: 'investor',     align: 'left',  getValue: (r) => investorLabel(r) },
  { key: 'payment_date', align: 'left',  getValue: (r) => r.payment_date },
  { key: 'amount',       align: 'right', getValue: (r) => r.payment_amount != null ? parseFloat(r.payment_amount) : null },
  { key: 'matched_date', align: 'left',  getValue: (r) => r.processed_at },
  { key: 'matched_by',   align: 'left',  getValue: (r) => r.created_by_name || r.created_by },
  { key: 'reference',    align: 'left',  getValue: (r) => r.payment_reference },
  { key: 'status',       align: 'left',  getValue: (r) => r.payment_status },
];

const PENDING_WHT_COLUMNS = [
  { key: 'contract',       align: 'left',  getValue: (e) => e.contract_reference },
  { key: 'investor',       align: 'left',  getValue: (e) => investorLabel(e) },
  { key: 'interest_date',  align: 'left',  getValue: (e) => e.payment_date },
  { key: 'gross_interest', align: 'right', getValue: (e) => e.gross_interest_amount != null ? parseFloat(e.gross_interest_amount) : null },
  { key: 'wht_rate',       align: 'right', getValue: (e) => e.wht_rate_applied != null ? parseFloat(e.wht_rate_applied) : null },
  { key: 'wht_due',        align: 'right', getValue: (e) => e.wht_amount != null ? parseFloat(e.wht_amount) : null },
  { key: 'days_overdue',   align: 'left',  getValue: (e) => e.days_overdue != null ? Number(e.days_overdue) : 0 },
];

const MATCHED_WHT_COLUMNS = [
  { key: 'contract',     align: 'left',  getValue: (p) => p.contract_reference },
  { key: 'investor',     align: 'left',  getValue: (p) => investorLabel(p) },
  { key: 'payment_date', align: 'left',  getValue: (p) => p.payment_date },
  { key: 'amount',       align: 'right', getValue: (p) => p.payment_amount != null ? parseFloat(p.payment_amount) : null },
  { key: 'matched_date', align: 'left',  getValue: (p) => p.processed_at },
  { key: 'matched_by',   align: 'left',  getValue: (p) => p.processed_by || p.created_by },
  { key: 'reference',    align: 'left',  getValue: (p) => p.payment_reference },
  { key: 'status',       align: 'left',  getValue: (p) => p.payment_status },
];

function PaymentMatching() {
  const { tenant, isFeatureEnabled, isWorkflowDocumentRequired } = useTenant();
  // Matching is a business-data operation and must not run on the master tenant
  // (same convention as Sidebar.js / App.js). The API also enforces this via
  // requireOperatingTenant; this guard keeps the UI from offering or loading it.
  const isMasterTenant = tenant?.isMaster === true;
  const notify = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [paymentsSubTab, setPaymentsSubTab] = useState(0);
  const [capitalSubTab, setCapitalSubTab] = useState(0);
  const matchedPaymentsSort = useTableSort();
  const pendingDrawDownsSort = useTableSort();
  const receiptsSort = useTableSort();
  const pendingWhtSort = useTableSort();
  const matchedWhtSort = useTableSort();
  const [loading, setLoading] = useState(false);
  // Distinct from `loading`: flips true once the payment feeds have loaded at
  // least once. `loading` starts false, so it's an unreliable "data ready"
  // signal for the scheduleId deeplink effect (which would otherwise search
  // empty feeds on cold mount and wrongly conclude "not found").
  const [dataLoaded, setDataLoaded] = useState(false);
  // Each independently-paged table owns its own pagination instance (they
  // paginate different arrays, so they can't share one). All default to 25/page
  // and inherit the standard rowsPerPageOptions ([5,10,25,50]) from the hook.
  const matchedPg = usePagination({ defaultRowsPerPage: 25 });
  const pendingDrawDownPg = usePagination({ defaultRowsPerPage: 25 });
  const receiptPg = usePagination({ defaultRowsPerPage: 25 });
  const pendingWhtPg = usePagination({ defaultRowsPerPage: 25 });
  const matchedWhtPg = usePagination({ defaultRowsPerPage: 25 });

  // Horizon for the three "upcoming" feeds (interest / combined / capital-out).
  // Sent to the API as ?days_ahead=; 'all' removes the upper bound (T3-104).
  // Default '30' deliberately preserves the pre-T3-104 behaviour on load — the
  // bug was that 30 days was the ONLY option, not that 30 is a bad default:
  // a row due further out appeared in neither the overdue nor the upcoming feed
  // and so was invisible here while the contract view showed it unmatched.
  // Fixed allowlist, not free input: it is interpolated into the query string.
  const PAYMENT_WINDOWS = ['30', '60', '90', 'all'];
  const [paymentWindow, setPaymentWindow] = useState('30');

  // Unified MatchDialog state. matchType picks the endpoint and which sections
  // the dialog renders (Combined gets apportionment; variance section auto-
  // unfolds for any match where cash differs from expected).
  const [matchDialogOpenUnified, setMatchDialogOpenUnified] = useState(false);
  const [matchDialogType, setMatchDialogType] = useState('payment');
  const [matchDialogRow, setMatchDialogRow] = useState(null);
  const [matchDialogSubmitting, setMatchDialogSubmitting] = useState(false);
  // Reapportion mode state — set when opening the dialog to edit an existing
  // match. Cleared on close so the next 'Match' click starts fresh.
  const [matchDialogMode, setMatchDialogMode] = useState('match'); // 'match' | 'reapportion'
  const [matchDialogPaymentId, setMatchDialogPaymentId] = useState(null);
  const [matchDialogInitialPayment, setMatchDialogInitialPayment] = useState(null);
  const [matchDialogInitialAllocation, setMatchDialogInitialAllocation] = useState(null);
  const [matchDialogInitialReason, setMatchDialogInitialReason] = useState('');
  // T117: settlement currency for the contract behind the open dialog (null/'' => single-ccy).
  // Hold both code (for the dialog UI) and id (for the match endpoint payload).
  const [matchDialogSettlementCcy, setMatchDialogSettlementCcy] = useState('');
  const [matchDialogSettlementId, setMatchDialogSettlementId] = useState(null);

  // Data states
  const [upcomingPayments, setUpcomingPayments] = useState([]);
  const [overduePayments, setOverduePayments] = useState([]);
  const [matchedPayments, setMatchedPayments] = useState([]);

  // Combined Payments tab state (annuity / combine_payment_rows contracts)
  const [combinedOverdue, setCombinedOverdue] = useState([]);
  const [combinedUpcoming, setCombinedUpcoming] = useState([]);
  const [combinedSubTab, setCombinedSubTab] = useState(0);

  // Global contract search. Filters every visible row across every tab by
  // case-insensitive token match on contract_reference / user_reference /
  // investor_name. Pure client-side filter of rows already loaded.
  const [matchingSearch, setMatchingSearch] = useState('');

  // Capital tab — direction switch (in / out). 'in' shows the existing
  // drawdown receipts flow; 'out' shows outgoing Capital Payment / Adhoc
  // Capital Withdrawal rows (Phase 9). Out → uses the same MatchDialog as
  // the Payments tab (matchType='payment') and the same sp_payment_match_inline.
  const [capitalDirection, setCapitalDirection] = useState('in');
  const [capitalOutOverdue, setCapitalOutOverdue] = useState([]);
  const [capitalOutUpcoming, setCapitalOutUpcoming] = useState([]);
  const [capitalOutSubTab, setCapitalOutSubTab] = useState(0);

  // Receipts tab state
  const [receipts, setReceipts] = useState([]);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [pendingDrawDowns, setPendingDrawDowns] = useState([]); // Pending capital draw downs from amortization schedule
  const [selectedDrawDown, setSelectedDrawDown] = useState(null); // For receipting a specific draw down
  // T117: capital receipt in a foreign settlement currency. When the contract has a settlement
  // currency, the user enters the foreign cash + dealt rate; `amount` (RHF) becomes the derived
  // contract-currency equivalent. settlementId/Ccy null => single-currency receipt.
  const [receiptSettlementCcy, setReceiptSettlementCcy] = useState('');
  const [receiptSettlementId, setReceiptSettlementId] = useState(null);
  const [receiptSettlementAmount, setReceiptSettlementAmount] = useState('');
  const [receiptFxRate, setReceiptFxRate] = useState('');

  // WHT tab state
  const [whtSubTab, setWhtSubTab] = useState(0);
  const [feeSubTab, setFeeSubTab] = useState(0);
  const [pendingWht, setPendingWht] = useState([]);
  const [matchedWht, setMatchedWht] = useState([]);
  // Feature 30 Phase 4 — broker fee settlement, and the tenant switch that gates its tab.
  const [pendingFees, setPendingFees] = useState([]);
  const [matchedFees, setMatchedFees] = useState([]);
  const [feeSchedulingEnabled, setFeeSchedulingEnabled] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Document preview & download (shared hook)
  const docActions = useDocumentActions();

  const { control: receiptControl, handleSubmit: handleReceiptSubmit, reset: resetReceipt } = useForm({
    defaultValues: {
      contract_id: null,
      amount: '',
      received_date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      payment_method: 'EFT',
      notes: '',
    },
  });


  useEffect(() => {
    // Don't fetch business data on the master tenant — the page renders a notice.
    if (isMasterTenant) return;
    loadAllPaymentData();
    // paymentWindow is a dep because the horizon is applied server-side (the
    // "all" set is far larger than 30 days' worth), so changing it must refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMasterTenant, paymentWindow]);

  const loadAllPaymentData = async () => {
    try {
      setLoading(true);
      // Guard against an unexpected state value reaching the query string.
      const win = PAYMENT_WINDOWS.includes(paymentWindow) ? paymentWindow : '30';
      const [upcoming, overdue, matched, receiptsData, pendingDrawDownsData, pendingWhtData, matchedWhtData, combinedOverdueData, combinedUpcomingData, capitalOutOverdueData, capitalOutUpcomingData, feePendingData, feeMatchedData, workflowSettings] = await Promise.all([
        apiGet(`/payments/upcoming?days_ahead=${win}`).catch(() => ({ data: [] })),
        apiGet('/payments/overdue').catch(() => ({ data: [] })),
        apiGet('/payments?status=Matched&limit=100000').catch(() => ({ data: [] })),
        apiGet('/payments/receipts?limit=100000').catch(() => ({ data: [] })),
        // Get pending capital draw downs from amortization schedule (not yet receipted)
        apiGet('/payments/receipts/pending').catch(() => ({ data: [] })),
        apiGet('/payments/wht/pending').catch(() => ({ data: [] })),
        apiGet('/payments/wht/matched?limit=100000').catch(() => ({ data: [] })),
        apiGet('/payments/combined/overdue').catch(() => ({ data: [] })),
        apiGet(`/payments/combined/upcoming?days_ahead=${win}`).catch(() => ({ data: [] })),
        apiGet('/payments/capital-out/overdue').catch(() => ({ data: [] })),
        apiGet(`/payments/capital-out/upcoming?days_ahead=${win}`).catch(() => ({ data: [] })),
        // Feature 30 Phase 4 — broker fee settlement. Same empty-shape fallbacks as every feed
        // above, so a fee problem can never blank the whole matching screen.
        apiGet('/payments/fees/pending').catch(() => ({ data: [] })),
        apiGet('/payments/fees/matched').catch(() => ({ data: [] })),
        // The Broker Fees tab is gated on the SAME tenant switch as the rest of fee scheduling
        // rather than a new payments-module feature key: a tenant that has not enabled fee
        // scheduling has no fee schedules at all, so a second switch could only ever disagree
        // with the first and show an empty tab.
        apiGet('/contracts/settings/workflow').catch(() => ({ data: {} })),
      ]);

      setUpcomingPayments(Array.isArray(upcoming.data) ? upcoming.data : []);
      setOverduePayments(Array.isArray(overdue.data) ? overdue.data : []);
      setMatchedPayments(Array.isArray(matched.data) ? matched.data.filter(p => p.payment_type !== 'WHT') : []);
      // Normalise Capital Receipt field names so they line up with the rest of
      // the matching tables (payment_date / processed_at / payment_amount /
      // currency_code / payment_reference). The receipt SP returns the native
      // receipt-side names; this shim keeps it untouched.
      const normaliseReceipt = (r) => ({
        ...r,
        payment_date: r.payment_date || r.received_date,
        processed_at: r.processed_at || r.created_at,
        payment_amount: r.payment_amount != null ? r.payment_amount : r.amount,
        payment_reference: r.payment_reference || r.reference,
        currency_code: r.currency_code || r.currency,
        /* CAPITAL RECEIPTS, not payments — left on the scalar deliberately. capital_receipts
           carries its own proof_document_id INTEGER with a real FK and is out of T3-129's scope;
           PaymentProofCell.toProofIds falls back to this mirror field, so receipts still render. */
        proof_of_payment_document: r.proof_of_payment_document || r.proof_document_id,
      });
      setReceipts(Array.isArray(receiptsData.data) ? receiptsData.data.map(normaliseReceipt) : []);
      setPendingDrawDowns(Array.isArray(pendingDrawDownsData.data) ? pendingDrawDownsData.data : []);
      setPendingWht(Array.isArray(pendingWhtData.data) ? pendingWhtData.data : []);
      setMatchedWht(Array.isArray(matchedWhtData.data) ? matchedWhtData.data : []);
      setPendingFees(Array.isArray(feePendingData.data) ? feePendingData.data : []);
      setMatchedFees(Array.isArray(feeMatchedData.data) ? feeMatchedData.data : []);
      setFeeSchedulingEnabled(workflowSettings?.data?.enableBrokerFeeScheduling === true);
      setCombinedOverdue(Array.isArray(combinedOverdueData.data) ? combinedOverdueData.data : []);
      setCombinedUpcoming(Array.isArray(combinedUpcomingData.data) ? combinedUpcomingData.data : []);
      setCapitalOutOverdue(Array.isArray(capitalOutOverdueData.data) ? capitalOutOverdueData.data : []);
      setCapitalOutUpcoming(Array.isArray(capitalOutUpcomingData.data) ? capitalOutUpcomingData.data : []);
    } catch (err) {
      logger.error('Error loading payment data', { message: err.message, status: err.response?.status });
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  };

  // When a draw down is selected for receipt (from the pending table), open dialog with pre-filled data
  const handleReceiptDrawDown = async (drawDown) => {
    setSelectedDrawDown(drawDown);
    setReceiptFile(null);
    resetReceipt({
      contract_id: drawDown.contract_id,
      amount: drawDown.capital_amount || '',
      received_date: format(new Date(), 'yyyy-MM-dd'),
      reference: '',
      payment_method: 'EFT',
      notes: '',
    });
    // T117/T119: resolve the contract's settlement currency; if set, the dialog shows the
    // foreign amount + rate and `amount` becomes the derived contract-currency equivalent.
    // The capital receipt is the INITIAL CAPITAL, so its rate defaults to the contract's stored
    // settlement_fx_rate (the agreed initiation rate) — NOT a live lookup. Falls back to the
    // live rate only if the contract has no stored rate. Still user-overridable.
    setReceiptSettlementCcy('');
    setReceiptSettlementId(null);
    setReceiptSettlementAmount('');
    setReceiptFxRate('');
    if (drawDown?.contract_id) {
      try {
        const rows = await apiGet(`/contracts/${drawDown.contract_id}`).catch(() => null);
        const c = rows?.data || rows;
        if (c?.settlement_currency_code && c?.settlement_currency_id) {
          setReceiptSettlementCcy(c.settlement_currency_code);
          setReceiptSettlementId(c.settlement_currency_id);
          const capital = parseFloat(drawDown.capital_amount) || 0;
          const applyRate = (r) => {
            if (!(Number.isFinite(r) && r > 0)) return;
            setReceiptFxRate(String(r));
            // Auto-fill the foreign amount so the equivalent ≈ the expected capital.
            if (capital > 0) setReceiptSettlementAmount((capital / r).toFixed(2));
          };
          if (c.settlement_fx_rate != null && parseFloat(c.settlement_fx_rate) > 0) {
            applyRate(parseFloat(c.settlement_fx_rate));  // stored initiation rate
          } else {
            // Fallback: no stored rate → suggest the live rate.
            apiGet(`/exchange-rates/current/${c.settlement_currency_code}/${c.currency_code}`)
              .then((resp) => applyRate(parseFloat(resp?.rate?.rate)))
              .catch(() => { /* leave blank; user enters the rate */ });
          }
        }
      } catch { /* non-fatal: single-currency receipt */ }
    }
    setReceiptDialogOpen(true);
  };

  // Determine matchType from the schedule row's narration. Combined Payment
  // rows always get the Combined dialog; everything else uses the standard
  // Payments dialog. WHT has its own dedicated opener (handleOpenWhtMatchDialog).
  const matchTypeForRow = (schedule) => {
    const narration = (schedule?.narration || '').trim();
    if (narration === 'Combined Payment') return 'combined';
    // A fee row is identified by its own id field, not by narration: the fee feed returns
    // `fee_schedule_id` where the amortization feeds return `id`/`schedule_id`. That is a
    // structural signal rather than a string match, so it cannot be broken by a narration rename.
    if (schedule?.fee_schedule_id != null) return 'fee';
    return 'payment';
  };

  const handleOpenMatchDialog = async (schedule) => {
    setMatchDialogRow(schedule);
    setMatchDialogType(matchTypeForRow(schedule));
    // T117: the payment feeds don't carry the contract's settlement currency. Resolve it
    // from the contract so the dialog can offer the foreign-settlement + FX-rate fields.
    setMatchDialogSettlementCcy('');
    setMatchDialogSettlementId(null);
    if (schedule?.contract_id) {
      try {
        const rows = await apiGet(`/contracts/${schedule.contract_id}`).catch(() => null);
        const c = rows?.data || rows;
        if (c?.settlement_currency_code && c?.settlement_currency_id) {
          setMatchDialogSettlementCcy(c.settlement_currency_code);
          setMatchDialogSettlementId(c.settlement_currency_id);
        }
      } catch { /* non-fatal: dialog falls back to single-currency */ }
    }
    setMatchDialogOpenUnified(true);
  };

  const handleCloseUnifiedMatch = () => {
    if (matchDialogSubmitting) return;
    setMatchDialogOpenUnified(false);
    setMatchDialogRow(null);
    setMatchDialogMode('match');
    setMatchDialogPaymentId(null);
    setMatchDialogInitialPayment(null);
    setMatchDialogInitialAllocation(null);
    setMatchDialogInitialReason('');
    setMatchDialogSettlementCcy('');
    setMatchDialogSettlementId(null);
  };

  // Open MatchDialog in read-only view mode for any matched row. Works
  // across Payments / Combined / WHT / Capital Receipts. Capital receipts
  // don't have a schedule_id we can resolve to expected interest/capital,
  // so we just render the metadata we already have on the receipt row.
  const handleOpenViewDialog = async (matched, viewMatchType = 'payment') => {
    try {
      // T3-105: was the WHT leg actually remitted, and for how much? Answered entirely in memory —
      // matchedWht is already loaded in the same Promise.all as the matched payments, and its rows
      // carry the schedule_id the two legs share. No extra call. Resolved once here so both the
      // resolved-schedule branch and the fallback can use it.
      const whtLeg = matched.schedule_id
        ? matchedWht.find(w => w.schedule_id === matched.schedule_id)
        : null;
      const whtSettlement = whtLeg
        ? {
            amount: whtLeg.payment_amount,
            date: whtLeg.payment_date,
            reference: whtLeg.payment_reference,
          }
        : null;

      let scheduleRow = null;
      if (matched.contract_id && matched.schedule_id && viewMatchType !== 'capital-receipt') {
        const scheduleResp = await apiGet(`/amortization/contract/${matched.contract_id}`).catch(() => null);
        const scheduleRows = scheduleResp?.data?.schedule || scheduleResp?.data || [];
        const entry = Array.isArray(scheduleRows)
          ? scheduleRows.find(r => r.id === matched.schedule_id)
          : null;
        if (entry) {
          scheduleRow = {
            id: entry.id,
            contract_id: matched.contract_id,
            contract_reference: matched.contract_reference,
            user_reference: matched.user_reference,
            investor_name: matched.investor_name,
            payment_date: entry.payment_date,
            scheduled_amount: entry.payment_amount,
            payment_amount: entry.payment_amount,
            interest_amount: entry.interest_amount,
            capital_amount: entry.capital_amount,
            currency: matched.currency_code,
            narration: entry.narration,
            // T3-105: the WHT context the dialog's panel renders from. fn_amortization_get_schedule
            // already projects all five — they were simply not being copied across, so the panel
            // (gated on wht_amount > 0) never rendered when viewing a matched payment.
            gross_interest_amount: entry.gross_interest_amount,
            wht_amount: entry.wht_amount,
            net_interest_amount: entry.net_interest_amount,
            wht_rate_applied: entry.wht_rate_applied,
            wht_gross_up: entry.wht_gross_up,
            wht_paid: !!whtLeg,
            _wht_settlement: whtSettlement,
          };
        }
      }
      if (!scheduleRow) {
        // Capital receipts (or any row where we couldn't resolve the schedule)
        // — render whatever we have, no apportionment context.
        scheduleRow = {
          id: matched.schedule_id || null,
          contract_id: matched.contract_id,
          contract_reference: matched.contract_reference,
          user_reference: matched.user_reference,
          investor_name: matched.investor_name,
          payment_date: matched.payment_date,
          scheduled_amount: matched.payment_amount,
          payment_amount: matched.payment_amount,
          interest_amount: 0,
          capital_amount: 0,
          currency: matched.currency_code,
          narration: viewMatchType === 'capital-receipt' ? 'Capital Receipt' : '',
          // T3-105: zeroed explicitly, mirroring interest/capital above. Without this a capital
          // receipt (or an unresolvable row) would inherit nothing and the panel would correctly
          // hide — but being explicit stops a future edit from letting stale values leak through.
          // NOTE: this branch is also reached when the schedule entry cannot be found, which
          // happens for a payment matched against a row later superseded by a regen —
          // fn_amortization_get_schedule filters is_historical. That is a PRE-EXISTING hole
          // affecting interest/capital identically; not introduced or fixed here.
          gross_interest_amount: 0,
          wht_amount: 0,
          net_interest_amount: 0,
          wht_rate_applied: null,
          wht_gross_up: false,
          wht_paid: !!whtLeg,
          _wht_settlement: whtSettlement,
        };
      }
      setMatchDialogRow(scheduleRow);
      setMatchDialogType(viewMatchType === 'capital-receipt' ? 'payment' : viewMatchType);
      setMatchDialogMode('view');
      setMatchDialogPaymentId(matched.id);

      // T117/T119: surface the settlement currency + dealt rate/amount read-only on the view.
      // Prefer the row's actual dealt values (a matched row carries them). When the row has
      // none (pending/scheduled rows, legacy NULL rows, or feeds that don't project them),
      // fall back to resolving the contract's settlement currency so a multi-currency capital
      // row still shows the FX context read-only — dealt rate if present, else the contract's
      // stored initial-capital rate, else the live rate.
      let viewSettleCcy = matched.settlement_currency_code || '';
      let viewSettleAmount = matched.settlement_amount;
      let viewFxRate = matched.fx_rate;
      if (!viewSettleCcy && matched.contract_id) {
        try {
          const cRows = await apiGet(`/contracts/${matched.contract_id}`).catch(() => null);
          const c = cRows?.data || cRows;
          if (c?.settlement_currency_code && c?.settlement_currency_code !== c?.currency_code) {
            viewSettleCcy = c.settlement_currency_code;
            if (viewFxRate == null) {
              if (c.settlement_fx_rate != null && parseFloat(c.settlement_fx_rate) > 0) {
                viewFxRate = c.settlement_fx_rate;  // stored initial-capital rate
              } else {
                const fxResp = await apiGet(`/exchange-rates/current/${c.settlement_currency_code}/${c.currency_code}`).catch(() => null);
                const live = parseFloat(fxResp?.rate?.rate);
                if (Number.isFinite(live) && live > 0) viewFxRate = live;
              }
            }
            // Derive the read-only foreign amount from the equivalent ÷ rate when not stored.
            if (viewSettleAmount == null && viewFxRate && parseFloat(viewFxRate) > 0) {
              const equiv = parseFloat(matched.payment_amount) || 0;
              if (equiv > 0) viewSettleAmount = +(equiv / parseFloat(viewFxRate)).toFixed(2);
            }
          }
        } catch { /* non-fatal: falls back to single-currency view */ }
      }
      setMatchDialogSettlementCcy(viewSettleCcy);
      setMatchDialogInitialPayment({
        payment_amount: matched.payment_amount,
        payment_date: matched.payment_date,
        payment_reference: matched.payment_reference,
        payment_method: matched.payment_method,
        notes: matched.notes,
        settlement_amount: viewSettleAmount,
        fx_rate: viewFxRate,
        // "Matched by" — the user who processed the match. Capital receipts carry a
        // resolved name (created_by_name); payments/WHT carry the acting user's email
        // on processed_by (fallback created_by).
        matched_by: matched.processed_by || matched.created_by_name || matched.created_by || null,
      });
      setMatchDialogInitialAllocation({
        allocated_interest: matched.allocated_interest != null ? matched.allocated_interest : 0,
        allocated_capital: matched.allocated_capital != null ? matched.allocated_capital : 0,
      });
      setMatchDialogInitialReason(matched.variance_reason || '');
      setMatchDialogOpenUnified(true);
    } catch (err) {
      logger.error('Error opening view dialog', { message: err.message, status: err.response?.status });
    }
  };

  // Open MatchDialog in reapportion mode for a matched payment row.
  // Fetches the schedule entry for the row's contract so we have the expected
  // interest/capital values for the dialog header + quick-allocate buttons.
  const handleOpenReapportionDialog = async (matchedPayment) => {
    try {
      const scheduleResp = await apiGet(`/amortization/contract/${matchedPayment.contract_id}`).catch(() => null);
      const scheduleRows = scheduleResp?.data?.schedule || scheduleResp?.data || [];
      const entry = Array.isArray(scheduleRows)
        ? scheduleRows.find(r => r.id === matchedPayment.schedule_id)
        : null;

      if (!entry) {
        notify.error('Could not load the schedule entry for this match.');
        return;
      }

      setMatchDialogRow({
        id: entry.id,
        contract_id: matchedPayment.contract_id,
        contract_reference: matchedPayment.contract_reference,
        user_reference: matchedPayment.user_reference,
        investor_name: matchedPayment.investor_name,
        payment_date: entry.payment_date,
        scheduled_amount: entry.payment_amount,
        payment_amount: entry.payment_amount,
        interest_amount: entry.interest_amount,
        capital_amount: entry.capital_amount,
        currency: matchedPayment.currency_code,
        narration: entry.narration,
      });
      setMatchDialogType(entry.narration === 'Combined Payment' ? 'combined' : 'payment');
      setMatchDialogMode('reapportion');
      setMatchDialogPaymentId(matchedPayment.id);
      // T117: reapportion is rate-locked; show the dealt settlement read-only for context.
      setMatchDialogSettlementCcy(matchedPayment.settlement_currency_code || entry.settlement_currency_code || '');
      setMatchDialogSettlementId(matchedPayment.settlement_currency_id || null);
      setMatchDialogInitialPayment({
        payment_amount: matchedPayment.payment_amount,
        payment_date: matchedPayment.payment_date,
        payment_reference: matchedPayment.payment_reference,
        payment_method: matchedPayment.payment_method,
        notes: matchedPayment.notes,
        settlement_amount: matchedPayment.settlement_amount,
        fx_rate: matchedPayment.fx_rate,
      });
      setMatchDialogInitialAllocation({
        allocated_interest: matchedPayment.allocated_interest != null
          ? matchedPayment.allocated_interest
          : Math.min(parseFloat(matchedPayment.payment_amount) || 0, parseFloat(entry.interest_amount) || 0),
        allocated_capital: matchedPayment.allocated_capital != null
          ? matchedPayment.allocated_capital
          : Math.max(0, (parseFloat(matchedPayment.payment_amount) || 0) - Math.min(parseFloat(matchedPayment.payment_amount) || 0, parseFloat(entry.interest_amount) || 0)),
      });
      setMatchDialogInitialReason(matchedPayment.variance_reason || '');
      setMatchDialogOpenUnified(true);
    } catch (err) {
      logger.error('Error opening reapportion dialog', { message: err.message, status: err.response?.status });
      notify.error('Failed to load match for editing');
    }
  };

  // Unified MatchDialog submit handler. Receives a structured payload:
  //   { payment: {...}, allocation?: {...}, variance?: {...} }
  // and routes to the right endpoint based on matchDialogType / matchDialogMode.
  const handleUnifiedMatchSubmit = async ({ payment, allocation, variance, wht, group, carry_forward_shortfall }) => {
    if (!matchDialogRow) return;
    try {
      setMatchDialogSubmitting(true);

      // Reapportion mode: only allocation + variance reason are mutable.
      // Hits PUT /payments/:id/apportionment instead of POST /payments/match.
      if (matchDialogMode === 'reapportion' && matchDialogPaymentId) {
        // For Combined rows the dialog supplies an allocation block. For
        // single-bucket rows the SP can derive defaults — but we still need
        // to send concrete values that sum to payment_amount. Fall back to
        // the existing initialAllocation for non-Combined paths.
        const alloc = allocation || matchDialogInitialAllocation || {};
        await apiPut(`/payments/${matchDialogPaymentId}/apportionment`, {
          allocated_interest: alloc.allocated_interest,
          allocated_capital: alloc.allocated_capital,
          variance_reason: variance?.reason || null,
        });
        handleCloseUnifiedMatch();
        await loadAllPaymentData();
        setMatchDialogSubmitting(false);
        return;
      }

      // Per-tenant workflow requirement: refuse to match without proof when
      // the workflow has is_required = true.
      // T3-105: when the WHT leg is included, ONE document covers both legs, so the requirement is
      // satisfied if EITHER workflow demands a proof and a file is attached. The tenant commonly has
      // payment_match optional but wht_match required, so ticking the box makes a proof mandatory
      // that would not otherwise be — check both codes rather than just the dialog's own.
      const workflowCode = matchDialogType === 'wht' ? 'wht_match' : 'payment_match';
      const proofRequiredHere = isWorkflowDocumentRequired(workflowCode)
        || (!!wht && isWorkflowDocumentRequired('wht_match'));
      /* T3-129: proof_files (plural) — a match can carry several documents. ⚠ The dialog RENAMED
         the key from proof_file deliberately: an empty array is truthy in JS, so a stale
         `payment.proof_file` test would have passed with zero files attached. */
      const proofFiles = payment.proof_files || [];
      const whtProofFiles = wht?.proof_files || [];
      if (proofFiles.length === 0 && whtProofFiles.length === 0 && proofRequiredHere) {
        notify.error(wht
          ? 'A proof of payment document is required — it covers both the interest payment and the withholding tax.'
          : 'A proof of payment document is required to match this payment.');
        setMatchDialogSubmitting(false);
        return;
      }

      /* T3-129: upload every staged proof, then match. FAIL LOUD — this previously swallowed the
         upload error and matched anyway with proof_document_id: null, which defeated the
         required-proof gate enforced immediately above and silently lost the operator's document.
         With N files that failure surface multiplies, so any failure now ABORTS the match: the
         dialog stays open with the files still staged and the message names the offending file.

         ⚠ Accepted residue: if file 3 of 5 fails, files 1-2 are already uploaded client_documents
         rows that no payment references. They remain valid contract documents and appear in the
         contract's document list; a compensating-delete loop could itself fail part-way, which is
         a worse failure mode than a couple of unreferenced documents.

         Sequential rather than parallel: each upload does its own investor resolution, blob write
         and sp_document_create, and a named per-file error is more useful than an aggregate. */
      const proofDocumentIds = [];
      for (const file of proofFiles) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', matchDialogType === 'wht' ? 'proof_of_wht_payment' : 'proof_of_payment');
        formData.append('contract_id', matchDialogRow.contract_id);
        formData.append('notes', `Proof of payment for ${matchDialogRow.contract_reference || ''} - ${payment.payment_reference || 'No reference'}`);

        let uploadResult;
        try {
          uploadResult = await apiUpload('/admin/documents/upload', formData);
        } catch (uploadErr) {
          logger.error('Error uploading proof', {
            message: uploadErr.message, status: uploadErr.response?.status, fileName: file.name,
          });
          const serverMessage = uploadErr.response?.data?.error?.message || uploadErr.message;
          notify.error(`Could not upload "${file.name}"${serverMessage ? `: ${serverMessage}` : ''}. The payment was NOT matched — your files are still attached, please try again.`);
          setMatchDialogSubmitting(false);
          return;
        }
        // A 200 with no id is still a failure (same guard as SubmitForApprovalDialog).
        const docId = uploadResult?.data?.id;
        if (!docId) {
          logger.error('Proof upload returned no document id', { fileName: file.name });
          notify.error(`Upload of "${file.name}" returned no document id. The payment was NOT matched — please try again.`);
          setMatchDialogSubmitting(false);
          return;
        }
        proofDocumentIds.push(docId);
      }

      // ⚠ ONE OF FOUR PLACES THAT ENUMERATE PAYMENT KINDS. The others are the tab registry
      // (allPaymentTabs), matchTypeForRow above, and PAYMENT_NARRATIONS in utils/paymentHelpers.js.
      // Missing any one of them produces a SILENT misclassification, not an error — so they move
      // together or not at all.
      const MATCH_ENDPOINTS = {
        wht: '/payments/wht/match',
        fee: '/payments/fees/match',
      };
      const endpoint = MATCH_ENDPOINTS[matchDialogType] || '/payments/match';
      const payload = {
        // T3-109: a GROUP line deliberately carries no schedule id — it stands for N of them, and
        // the legs below name each one. groupScheduleRowsByContractDate nulls `id` precisely so a
        // path that forgot the group would fail loudly here rather than silently matching against
        // an arbitrary member's row.
        // ⚠ A FEE SETTLES A contract_fee_schedule ROW, NOT AN AMORTIZATION ONE. The fee endpoint
        // therefore takes `fee_schedule_id` — sending `schedule_id` would be silently ignored and
        // the request rejected as missing a required field. Both keys are set from the same row id
        // because the dialog is shared; the API reads only the one it expects.
        ...(matchDialogType === 'fee'
          ? { fee_schedule_id: matchDialogRow.id }
          : { schedule_id: matchDialogRow.id }),
        payment_amount: payment.payment_amount,
        payment_date: payment.payment_date,
        payment_reference: payment.payment_reference,
        payment_method: payment.payment_method,
        notes: payment.notes,
        // T3-129: the full set. The API also still accepts the legacy scalar proof_document_id,
        // but a current client sends only the array.
        proof_document_ids: proofDocumentIds,
      };

      if (matchDialogType === 'combined' && allocation) {
        payload.allocated_interest = allocation.allocated_interest;
        payload.allocated_capital = allocation.allocated_capital;
      }
      if (variance?.reason) {
        payload.variance_reason = variance.reason;
      }
      // T117: multi-currency settlement. payment_amount above is already the contract-currency
      // equivalent the dialog computed; these record the foreign cash + dealt rate.
      if (payment.settlement_amount != null && payment.fx_rate != null && matchDialogSettlementId) {
        payload.settlement_currency_id = matchDialogSettlementId;
        payload.settlement_amount = payment.settlement_amount;
        payload.fx_rate = payment.fx_rate;
      }
      // T3-105: the withholding-tax leg, recorded in the SAME action. The API writes both legs in
      // one transaction, so either both land or neither does. Its own proof is uploaded separately
      // (the two legs are distinct cash movements and a tax audit wants the remittance document);
      // when none is supplied we fall back to the interest leg's proof, since in the common case
      // one payment advice covers both.
      if (wht) {
        payload.wht = {
          payment_amount: wht.payment_amount,
          payment_date: wht.payment_date,
          payment_reference: wht.payment_reference,
          // ONE document set covers both legs — the same proof ids are stored against the interest
          // row and the WHT row. A combined match is a single cash movement as far as the operator
          // is concerned, so the advice is uploaded once and referenced twice.
          proof_document_ids: proofDocumentIds,
        };
      }
      /* T3-109: the grouped multi-investor apportionment. Each leg names its OWN schedule row, so
         the API records one payment per investor in a single transaction — either the whole
         apportionment lands or none of it does. The one uploaded proof covers every leg, exactly
         as it covers both legs of a WHT match: it is one cash movement from the operator's side. */
      if (group?.legs?.length) {
        payload.group = {
          legs: group.legs.map((leg) => ({
            ...leg,
            /* T117: the dialog apportioned the foreign amount and supplied the shared rate; the
               currency ID is resolved here, where the contract was loaded. Attached per leg because
               sp_payment_match_inline validates and stores the settlement triple per row. */
            ...(leg.settlement_amount != null && matchDialogSettlementId
              ? { settlement_currency_id: matchDialogSettlementId }
              : {}),
            ...(leg.wht ? { wht: { ...leg.wht, proof_document_ids: proofDocumentIds } } : {}),
          })),
        };
      }

      /* T3-121: ask the API to raise a carry-forward of this payment's shortfall. A flag, not an
         amount — the API re-derives the residual from the schedule after the match commits. */
      if (carry_forward_shortfall) {
        payload.carry_forward_shortfall = true;
      }

      const matchResult = await apiPost(endpoint, payload);
      setMatchDialogOpenUnified(false);
      setMatchDialogRow(null);
      await loadAllPaymentData();

      /* T3-121: the payment and the carry-forward request succeed INDEPENDENTLY — the payment is
         committed before the request is raised. Report which actually happened rather than letting
         one imply the other. */
      if (carry_forward_shortfall) {
        const cf = matchResult?.data?.carry_forward;
        if (matchResult?.data?.carry_forward_error || cf === null) {
          notify.warning('Payment matched, but the carry-forward request could not be raised. The shortfall remains outstanding on this line — try again from the row.');
        } else if (Array.isArray(cf) && cf.length > 0) {
          const total = cf.reduce((s, r) => s + (parseFloat(r.residual) || 0), 0);
          notify.success(`Payment matched. ${formatNumber(total)} submitted for approval to carry to the next payment — this line stays outstanding until an approver applies it.`);
        } else {
          // Nothing was short once the cash landed, so there was nothing to carry.
          notify.success('Payment matched in full — no shortfall to carry forward.');
        }
      }
    } catch (err) {
      logger.error('Error matching payment', { message: err.message, status: err.response?.status });
      const serverMessage = err.response?.data?.error?.message || err.response?.data?.message || err.message;
      notify.error('Failed to match payment' + (serverMessage ? `: ${serverMessage}` : ''));
    } finally {
      setMatchDialogSubmitting(false);
    }
  };

  // T3-148: takes the whole payment row, not just an id, so the confirmation can name the
  // withholding-tax leg that will be reversed alongside it. A matched interest line can carry TWO
  // legs on one schedule_id -- the principal and the tax remittance -- and both count toward line
  // coverage (T3-106), so unmatching the principal now cascades to the tax leg and both return to
  // Pending. line_wht_amount is projected by fn_payment_get_list (T3-115) from the SAME expression
  // the DB cascade selects on, so the figure warned about and the figure reversed cannot disagree;
  // the matchedWht lookup is a cheap backstop for any row shape that lacks it.
  const handleUnmatchPayment = (payment) => {
    const whtLeg = payment?.schedule_id
      ? matchedWht.find(w => w.schedule_id === payment.schedule_id)
      : null;
    const whtAmount = parseFloat(payment?.line_wht_amount ?? whtLeg?.payment_amount) || 0;
    const ccy = payment?.currency_code || '';
    const hasWht = whtAmount > 0.005;

    setConfirmDialog({
      open: true,
      title: hasWht ? 'Unmatch Payment and WHT' : 'Unmatch Payment',
      message: hasWht
        ? `Are you sure you want to unmatch this payment? This will revert it to Pending status. `
          + `The withholding tax of ${ccy} ${formatNumber(whtAmount)} remitted on the same schedule `
          + `line will ALSO be unmatched and returned to Pending -- the tax remittance is reversed too.`
        : 'Are you sure you want to unmatch this payment? This will revert it to Pending status.',
      onConfirm: async () => {
        try {
          const resp = await apiPost(`/payments/${payment.id}/unmatch`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          if (resp?.data?.cascaded_wht_payment_id) {
            notify.success('Payment unmatched. Its withholding-tax leg was also returned to Pending.');
          }
          await loadAllPaymentData();
        } catch (err) {
          logger.error('Error unmatching payment', { message: err.message, status: err.response?.status });
          setConfirmDialog(prev => ({ ...prev, open: false }));
          notify.error('Failed to unmatch payment: ' + (err.response?.data?.error?.message || err.response?.data?.message || err.message));
        }
      },
    });
  };

  // Receipt handlers
  const handleCloseReceiptDialog = () => {
    setReceiptDialogOpen(false);
    setReceiptFile(null);
    setSelectedDrawDown(null);
    setReceiptSettlementCcy('');
    setReceiptSettlementId(null);
    setReceiptSettlementAmount('');
    setReceiptFxRate('');
    resetReceipt();
  };

  const handleCreateReceipt = async (data) => {
    // Get the contract/investor info from selectedDrawDown or from manual entry
    const contractId = selectedDrawDown?.contract_id || data.contract_id;
    const investorId = selectedDrawDown?.investor_id || data.investor_id;
    const contractRef = selectedDrawDown?.contract_reference || 'Manual';

    if (!investorId || !contractId) {
      notify.warning('Please select a contract');
      return;
    }

    // T117: in a multi-currency receipt the contract-currency `amount` is derived from the
    // foreign settlement amount × dealt rate; validate the foreign inputs.
    const isMultiCcyReceipt = !!receiptSettlementId;
    const settleAmt = parseFloat(receiptSettlementAmount) || 0;
    const settleRate = parseFloat(receiptFxRate) || 0;
    if (isMultiCcyReceipt && (settleAmt <= 0 || settleRate <= 0)) {
      notify.warning(`Enter the ${receiptSettlementCcy} amount received and the FX rate.`);
      return;
    }
    const contractEquivalent = isMultiCcyReceipt
      ? +(settleAmt * settleRate).toFixed(2)
      : parseFloat(data.amount);

    try {
      setUploadingReceipt(true);

      if (!receiptFile && isWorkflowDocumentRequired('capital_receipt')) {
        notify.error('A proof of receipt document is required to record a capital receipt.');
        setUploadingReceipt(false);
        return;
      }

      // Upload proof document first if provided
      let proofDocumentId = null;
      if (receiptFile) {
        const formData = new FormData();
        formData.append('file', receiptFile);
        formData.append('document_type', 'proof_of_receipt');
        formData.append('investor_id', investorId);
        formData.append('contract_id', contractId);
        formData.append('notes', `Capital receipt for ${contractRef} - ${data.reference || 'No reference'}`);

        try {
          const uploadResult = await apiUpload('/admin/documents/upload', formData);
          proofDocumentId = uploadResult.data?.id;
        } catch (uploadErr) {
          logger.error('Error uploading receipt proof', { message: uploadErr.message, status: uploadErr.response?.status });
        }
      }

      const payload = {
        investor_id: investorId,
        contract_id: contractId,
        schedule_id: selectedDrawDown?.schedule_id || null,
        amount: contractEquivalent,
        received_date: data.received_date,
        reference: data.reference,
        payment_method: data.payment_method,
        notes: data.notes,
        proof_document_id: proofDocumentId,
        // T117: foreign settlement metadata (amount above is the contract-currency equivalent).
        ...(isMultiCcyReceipt ? {
          settlement_currency_id: receiptSettlementId,
          settlement_amount: settleAmt,
          fx_rate: settleRate,
        } : {}),
      };

      await apiPost('/payments/receipts', payload);
      await loadAllPaymentData();
      handleCloseReceiptDialog();
    } catch (err) {
      logger.error('Error creating receipt', { message: err.message, status: err.response?.status });
      notify.error('Failed to create receipt: ' + (err.message || 'Unknown error'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleDeleteReceipt = (receiptId) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Receipt',
      message: 'Are you sure you want to delete this receipt?',
      onConfirm: async () => {
        try {
          await apiDelete(`/payments/receipts/${receiptId}`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadAllPaymentData();
        } catch (err) {
          logger.error('Error deleting receipt', { message: err.message, status: err.response?.status });
          setConfirmDialog(prev => ({ ...prev, open: false }));
          notify.error('Failed to delete receipt');
        }
      },
    });
  };

  // WHT handlers
  /**
   * Feature 30 Phase 4 — open the shared MatchDialog for a BROKER FEE row.
   *
   * Adapts the fee feed's shape to the schedule-row shape MatchDialog expects, exactly as the WHT
   * handler below does. The fee feed already returns the house triple
   * (scheduled_amount / matched_amount / outstanding_amount) under those names, so the dialog's
   * amount logic needs no fee-specific branch — only `id` has to be mapped, because a fee row is
   * keyed by fee_schedule_id.
   *
   * ⚠ NO settlement-currency lookup here, unlike the WHT handler. Fee matching is all-or-nothing
   * in this version with no FX leg (sp_fee_payment_match takes no settlement params), so offering
   * the foreign-settlement fields would collect values the API would silently discard.
   */
  const handleOpenFeeMatchDialog = (feeEntry) => {
    setMatchDialogRow({
      id: feeEntry.fee_schedule_id,
      // Kept so matchTypeForRow classifies a re-opened row as 'fee' on structure, not narration.
      fee_schedule_id: feeEntry.fee_schedule_id,
      contract_id: feeEntry.contract_id,
      contract_reference: feeEntry.contract_reference,
      investor_name: feeEntry.broker_name,
      payment_date: feeEntry.payment_date,
      scheduled_amount: feeEntry.scheduled_amount,
      matched_amount: feeEntry.matched_amount,
      payment_amount: feeEntry.outstanding_amount,
      interest_amount: 0,
      capital_amount: 0,
      currency_code: feeEntry.currency_code,
      narration: feeEntry.narration,
    });
    setMatchDialogType('fee');
    setMatchDialogSettlementCcy('');
    setMatchDialogSettlementId(null);
    setMatchDialogOpenUnified(true);
  };

  /**
   * Reverse a fee payment match.
   *
   * ⚠ This RE-OPENS the schedule row to deletion: sp_fee_schedule_clear refuses to remove a row
   * carrying a Matched fee payment, so unmatching withdraws that protection. No analogue exists
   * on the interest side, which is why it is called out rather than left implicit.
   */
  const handleUnmatchFee = (feePaymentId) => {
    // Confirmed first, matching handleUnmatchPayment below — reversing a settlement is
    // destructive and, for a fee, additionally re-opens the schedule row to deletion.
    setConfirmDialog({
      open: true,
      title: 'Unmatch Fee Payment',
      message: 'Are you sure you want to unmatch this broker fee payment? It will revert to '
        + 'Pending, and the fee schedule row will no longer be protected from regeneration.',
      onConfirm: async () => {
        try {
          await apiPost(`/payments/fees/${feePaymentId}/unmatch`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadAllPaymentData();
        } catch (err) {
          logger.error('Error unmatching fee payment', { message: err.message, status: err.response?.status });
          setConfirmDialog(prev => ({ ...prev, open: false }));
          notify.error('Failed to unmatch fee payment: ' + (err.response?.data?.message || err.message));
        }
      },
    });
  };

  const handleOpenWhtMatchDialog = async (whtEntry) => {
    // Adapt the WHT entry shape to the schedule-row shape MatchDialog expects.
    // The WHT match SP works against the same schedule_id, with the WHT amount
    // as the expected value.
    const adapted = {
      id: whtEntry.schedule_id,
      contract_id: whtEntry.contract_id,
      contract_reference: whtEntry.contract_reference,
      user_reference: whtEntry.user_reference,
      investor_name: whtEntry.investor_name,
      payment_date: whtEntry.payment_date,
      scheduled_amount: whtEntry.wht_amount,
      payment_amount: whtEntry.wht_amount,
      interest_amount: 0,
      capital_amount: 0,
      currency: whtEntry.currency_code,
      narration: 'Withholding Tax',
      _wht_context: {
        gross_interest: whtEntry.gross_interest_amount,
        wht_rate: whtEntry.wht_rate_applied,
        wht_amount: whtEntry.wht_amount,
      },
    };
    setMatchDialogRow(adapted);
    setMatchDialogType('wht');
    // T117: resolve the contract's settlement currency so WHT can also be settled in foreign ccy.
    setMatchDialogSettlementCcy('');
    setMatchDialogSettlementId(null);
    if (whtEntry?.contract_id) {
      try {
        const rows = await apiGet(`/contracts/${whtEntry.contract_id}`).catch(() => null);
        const c = rows?.data || rows;
        if (c?.settlement_currency_code && c?.settlement_currency_id) {
          setMatchDialogSettlementCcy(c.settlement_currency_code);
          setMatchDialogSettlementId(c.settlement_currency_id);
        }
      } catch { /* non-fatal */ }
    }
    setMatchDialogOpenUnified(true);
  };

  const handleUnmatchWht = (paymentId) => {
    setConfirmDialog({
      open: true,
      title: 'Unmatch WHT Payment',
      message: 'Are you sure you want to unmatch this WHT payment? The interest payment on the same '
        + 'schedule line stays matched -- the line will show as part paid until the tax is remitted again.',
      onConfirm: async () => {
        try {
          await apiPost(`/payments/${paymentId}/unmatch`);
          setConfirmDialog(prev => ({ ...prev, open: false }));
          await loadAllPaymentData();
        } catch (err) {
          logger.error('Error unmatching WHT payment', { message: err.message, status: err.response?.status });
          setConfirmDialog(prev => ({ ...prev, open: false }));
          notify.error('Failed to unmatch WHT payment: ' + (err.response?.data?.message || err.message));
        }
      },
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'partial':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  // Sub-feature tab filtering. 'combined' shares the 'matching' feature gate —
  // annuity contracts produce Combined Payment rows that belong to the same
  // payments workflow as Interest Payment rows.
  const allPaymentTabs = useMemo(() => [
    { feature: 'matching', label: 'Interest Payments', key: 'matching' },
    { feature: 'matching', label: 'Combined', key: 'combined' },
    { feature: 'capital_receipts', label: 'Capital', key: 'capital_receipts' },
    { feature: 'withholding_tax', label: 'Withholding Tax', key: 'withholding_tax' },
    // Feature 30 Phase 4. ⚠ Gated on a TENANT SETTING, not a payments-module feature key — hence
    // `alwaysFeature: true` below, which opts it out of the isFeatureEnabled filter. A tenant
    // without fee scheduling has no fee schedules at all, so a second module switch could only
    // ever disagree with the first and offer an empty tab.
    { feature: null, alwaysFeature: true, label: 'Broker Fees', key: 'broker_fees', tenantFlag: true },
  ], []);

  const visiblePaymentTabs = useMemo(() =>
    allPaymentTabs.filter(t => (t.tenantFlag ? feeSchedulingEnabled : isFeatureEnabled('payments', t.feature))),
    // ⚠ feeSchedulingEnabled MUST be in the deps: it arrives asynchronously with the feeds, so a
    // memo that ignored it would compute once with `false` and the Broker Fees tab would never
    // appear — a silent omission rather than a visible error.
    [allPaymentTabs, isFeatureEnabled, feeSchedulingEnabled]
  );

  const safeTabValue = visiblePaymentTabs.length > 0 ? Math.min(tabValue, visiblePaymentTabs.length - 1) : 0;
  const activePaymentFeature = visiblePaymentTabs[safeTabValue]?.key || visiblePaymentTabs[safeTabValue]?.feature;
  // Alias used by the summary StatCards.
  const paymentsTabKey = activePaymentFeature;

  // Track per-contract expansion state across the four Matched tabs. Default:
  // collapsed unless the contract has only one or two rows (auto-expanded so
  // small accounts stay visible). The user can toggle individually.
  const [groupExpanded, setGroupExpanded] = useState({});
  const toggleGroup = (key) => setGroupExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  /* Single-cell group header, for the grouped tables that have NOT gained the Allocated/Outstanding
   * columns (Combined Matched, Receipts, Matched WHT). Deliberately kept minimal — identity plus a
   * count — rather than carrying the old inline "Amount X · Allocated Y · Outstanding Z" text run,
   * because that text was the thing that read as messy: it floated into whatever whitespace sat on
   * the right of the table and lined up under unrelated headings. Those tables get the real columns
   * when they are widened; until then they state what they can state cleanly. */
  const renderLegacyGroupHeader = (group, colSpan, expanded) => (
    <TableRow
      key={`group-${group.key}`}
      hover
      onClick={(e) => { e.stopPropagation(); toggleGroup(group.key); }}
      sx={{ cursor: 'pointer', backgroundColor: '#fafafa', '&:hover': { backgroundColor: '#f0f0f0' } }}
    >
      <TableCell colSpan={colSpan} sx={{ py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
            {expanded ? '▾' : '▸'}
          </Typography>
          <ContractReferenceCell reference={group.contract_reference} userReference={group.user_reference} />
          <Typography variant="caption" color="textSecondary">· {investorLabel(group)}</Typography>
          <Typography variant="caption" color="textSecondary">
            · {group.rows.length} matched · {group.currency_code || ''} {formatNumber(group.total_amount)}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  /* Render a group header row for a Matched table.
   *
   * T3-114/T3-115: this used to be ONE colSpan cell holding a right-aligned run of inline text
   * ("1 matched · Amount X · Allocated Y · Outstanding Z"). That floated into whatever whitespace
   * happened to be on the right of the table, so the figures lined up under Proof/Status/Variance —
   * headings they have nothing to do with — while the unmatched queues showed the same triple as
   * proper right-aligned COLUMNS. Two presentations of one concept, and the messier one was the one
   * that reads like an accounting statement.
   *
   * Now it emits REAL cells positioned by the caller's `layout`, so the money lands under the money
   * headings on every tab. layout = { lead, amount, allocated, outstanding, status, trailing }
   * where each value is a count of blank filler cells (or a flag), letting each table place the
   * header against its own column set rather than hard-coding a second copy of that set here.
   *
   * Visual weight is a subtle tint with medium-weight figures: this is a subtotal line, and it
   * should read as one without competing with the payment rows beneath it.
   */
  const renderGroupHeader = (group, layout, expanded) => {
    /* Back-compat: three of the four grouped tables (Combined Matched, Receipts, Matched WHT) have
     * different column sets and have NOT gained the Allocated/Outstanding columns, so they still
     * pass a plain colSpan number. They keep the single-cell header; only the table that actually
     * has the columns renders into them. Widening those tables is a separate change — giving them a
     * header that spans columns they do not have would break their alignment, which is the very
     * defect being fixed here. */
    if (typeof layout === 'number') {
      return renderLegacyGroupHeader(group, layout, expanded);
    }
    const scheduled = parseFloat(group.scheduled_amount) || 0;
    const allocated = parseFloat(group.line_matched_amount) || 0;
    const wht = parseFloat(group.line_wht_amount) || 0;
    const carriedIn = parseFloat(group.residual_carried_in) || 0;
    const outstanding = +Math.max(0, scheduled - allocated).toFixed(2);
    const settled = scheduled > 0 && outstanding <= 0.01;
    const ccy = group.currency_code || '';
    // No obligation resolved (a legacy payment carrying no schedule_id): state only what is known
    // rather than implying a zero balance against an obligation we cannot see.
    const hasLine = scheduled > 0;
    const blanks = (n, prefix) => Array.from({ length: n }, (_, i) => (
      <TableCell key={`${prefix}-${i}`} sx={{ py: 1 }} />
    ));

    return (
      <TableRow
        key={`group-${group.key}`}
        hover
        onClick={(e) => { e.stopPropagation(); toggleGroup(group.key); }}
        sx={{ cursor: 'pointer', backgroundColor: '#fafafa', '&:hover': { backgroundColor: '#f0f0f0' } }}
      >
        {/* Identity: chevron · contract · investor, spanning the descriptive columns. */}
        <TableCell colSpan={layout.lead} sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
              {expanded ? '▾' : '▸'}
            </Typography>
            <ContractReferenceCell reference={group.contract_reference} userReference={group.user_reference} />
            <Typography variant="caption" color="textSecondary">· {investorLabel(group)}</Typography>
            <Typography variant="caption" color="textSecondary">
              · {group.rows.length} matched
            </Typography>
          </Box>
        </TableCell>

        {/* Amount / Allocated / Outstanding — under their own headings, same as the feed tabs. */}
        <TableCell align="right" sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight="medium">
            {hasLine ? `${ccy} ${formatNumber(scheduled)}` : ''}
          </Typography>
        </TableCell>
        {/* T3-122: how much of this line's obligation was rolled in from an EARLIER short payment.
            Without it, a line reading "100.00 of 100.00 — settled" hides that 34.72 of it was a
            previous period's shortfall, which is the reconciliation gap on this tab. */}
        <TableCell align="right" sx={{ py: 1 }}>
          {hasLine && carriedIn > 0.005 ? (
            <Tooltip title={`Includes R ${formatNumber(carriedIn)} rolled in from a prior override`}>
              <Typography variant="body2" fontWeight="medium" color="info.main">
                {formatNumber(carriedIn)}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="textSecondary">{hasLine ? '-' : ''}</Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ py: 1 }}>
          {hasLine && (
            <>
              <Typography variant="body2" fontWeight="medium" color="success.main">
                {formatNumber(allocated)}
              </Typography>
              {/* T3-115: the tax leg is real cash on this line but its row is filtered off this tab
                  (WHT has its own). Naming it here is what makes the total add up on screen:
                  interest + tax = allocated. Without it the difference reads as an error. */}
              {wht > 0 && (
                <Typography variant="caption" color="textSecondary" display="block">
                  incl. tax {formatNumber(wht)}
                </Typography>
              )}
            </>
          )}
        </TableCell>
        <TableCell align="right" sx={{ py: 1 }}>
          <Typography
            variant="body2"
            fontWeight="medium"
            color={!hasLine ? 'textSecondary' : (settled ? 'textSecondary' : 'warning.main')}
          >
            {hasLine ? formatNumber(outstanding) : ''}
          </Typography>
        </TableCell>

        {blanks(layout.beforeStatus || 0, 'bs')}

        {/* Settled / Part paid sits in the Status column, beside the rows' own Matched chips. */}
        <TableCell sx={{ py: 1 }}>
          {hasLine && (
            <Chip
              size="small"
              variant="outlined"
              color={settled ? 'success' : 'warning'}
              label={settled ? 'Settled' : 'Part paid'}
            />
          )}
        </TableCell>

        {blanks(layout.trailing || 0, 'tr')}
      </TableRow>
    );
  };

  // Default: every group starts collapsed. User toggles individually.
  const isGroupExpanded = (group) => !!groupExpanded[group.key];

  /* Group -> sort -> paginate, over GROUPS.
   *
   * T3-114: the four Matched tables did this in the wrong order — groupByContract(paginateData(...)) —
   * so pagination ran on individual PAYMENTS before they were grouped. A schedule line settled by two
   * payments that straddled a page boundary therefore became TWO groups on TWO pages, each showing
   * the same full header figures: the same allocated total counted twice, on a screen whose whole
   * purpose is to state what a line has been paid. Grouping first makes a group indivisible, and
   * paginating over groups means a page holds whole lines.
   *
   * Returns { groups, count } — count is the number of GROUPS, which is what TablePagination must be
   * given; passing the raw row count would page over a total that no longer matches what is rendered.
   */
  const groupPaginateSort = (rows, sortState, columns, pgState) => {
    const groups = groupByContract(rows);
    const sorted = sortState ? sortState.sortRows(groups, columns) : groups;
    return {
      groups: pgState ? pgState.paginateData(sorted) : sorted,
      count: groups.length,
    };
  };

  /* Group a Matched rowset by the SCHEDULE LINE each payment settles.
   *
   * T3-114: this keyed on CONTRACT alone, so every payment a contract had ever taken collapsed
   * into one header regardless of which obligation it settled — a header reading "5 matched" told
   * the user nothing about whether any particular line was finished. A line, not a contract, is
   * the thing that gets paid off: dev Med-SZ-2026-0008 schedule 1923 took 200.00 and then 58.90
   * against a 258.90 obligation, and THAT is the group worth seeing.
   *
   * Keyed on (contract_id, schedule_id) rather than schedule_id alone so a legacy payment with no
   * schedule_id still groups under its own contract instead of colliding with every other
   * unattached payment across the tenant.
   *
   * Each group carries the Amount / Allocated / Outstanding triple for its line, from the T3-114
   * scheduled_amount + line_matched_amount columns.
   */
  const groupByContract = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const map = new Map();
    const order = [];
    for (const r of rows) {
      const contractKey = r.contract_id || r.contract_reference || '__unknown__';
      const key = r.schedule_id ? `${contractKey}|s${r.schedule_id}` : `${contractKey}|unattached`;
      if (!map.has(key)) {
        order.push(key);
        map.set(key, {
          key,
          contract_reference: r.contract_reference,
          user_reference: r.user_reference,
          investor_name: r.investor_name,
          is_multi_investor: r.is_multi_investor,
          investor_count: r.investor_count,
          investor_names: r.investor_names,
          currency_code: r.currency_code,
          schedule_id: r.schedule_id || null,
          // The obligation and the cash against it are LINE-level and identical on every payment
          // in the group, so take them from the first row rather than summing — summing would
          // multiply the obligation by the number of payments that settled it.
          scheduled_amount: parseFloat(r.scheduled_amount) || 0,
          line_matched_amount: parseFloat(r.line_matched_amount) || 0,
          // T3-122: LINE-level, like the two above — several payments can settle one row, so this
          // is a property of the row they settle, not of any one payment.
          residual_carried_in: parseFloat(r.residual_carried_in) || 0,
          // T3-115: the tax portion of line_matched_amount. The Matched tab hides WHT rows (tax has
          // its own tab), so without this the header's allocated total could not be reconciled
          // against the rows on screen — 12,000.00 stated above a single visible 11,331.16.
          line_wht_amount: parseFloat(r.line_wht_amount) || 0,
          rows: [],
          total_amount: 0,
        });
      }
      const g = map.get(key);
      g.rows.push(r);
      g.total_amount += parseFloat(r.payment_amount) || 0;
    }
    /* Sorting now runs over GROUPS (T3-114 fixed the group-inside-pagination bug), and the column
     * definitions read payment-level fields — payment_date, processed_at, payment_reference and so
     * on. A group carries none of those, so every getValue would return undefined and the sort
     * would silently become a no-op that still LOOKS like it sorted.
     *
     * Expose a representative for each: the LATEST payment in the group, because that is what the
     * user sees as "when this line was last touched" and it keeps the ordering stable as a line
     * takes further payments. total_amount is exposed as payment_amount so an Amount sort orders
     * groups by the cash actually received against the line, not by whichever payment happened to
     * be first. */
    return order.map((k) => {
      const g = map.get(k);
      const latest = g.rows.reduce((a, b) => (
        new Date(b.payment_date || 0) >= new Date(a.payment_date || 0) ? b : a
      ), g.rows[0]);
      return {
        ...g,
        payment_date: latest?.payment_date,
        processed_at: latest?.processed_at,
        processed_by: latest?.processed_by,
        created_by: latest?.created_by,
        created_by_name: latest?.created_by_name,
        payment_reference: latest?.payment_reference,
        payment_status: latest?.payment_status,
        payment_amount: g.total_amount,
      };
    });
  };

  // Token-AND filter on contract_reference / user_reference / investor names.
  // Empty search returns the input array unchanged.
  // T3-109: the investor term goes through investorSearchText, which adds the
  // junction-derived investor_names. Without it a multi-investor contract whose
  // schedule is contract-level has no investor_name at all and was unfindable
  // by any of its holders' names.
  const applySearch = useMemo(() => {
    const raw = (matchingSearch || '').trim().toLowerCase();
    if (!raw) return (rows) => rows;
    const tokens = raw.split(/\s+/).filter(Boolean);
    return (rows) => (Array.isArray(rows) ? rows.filter((r) => {
      const hay = `${r.contract_reference || ''} ${r.user_reference || ''} ${investorSearchText(r)}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    }) : []);
  }, [matchingSearch]);

  // Filtered views used by every Matched / Overdue / Upcoming table.
  const fOverdue = useMemo(() => applySearch(overduePayments), [applySearch, overduePayments]);
  const fUpcoming = useMemo(() => applySearch(upcomingPayments), [applySearch, upcomingPayments]);
  const fMatched = useMemo(() => applySearch(matchedPayments), [applySearch, matchedPayments]);
  const fCombinedOverdue = useMemo(() => applySearch(combinedOverdue), [applySearch, combinedOverdue]);
  const fCombinedUpcoming = useMemo(() => applySearch(combinedUpcoming), [applySearch, combinedUpcoming]);
  const fCapitalOutOverdue = useMemo(() => applySearch(capitalOutOverdue), [applySearch, capitalOutOverdue]);
  const fCapitalOutUpcoming = useMemo(() => applySearch(capitalOutUpcoming), [applySearch, capitalOutUpcoming]);
  const fPendingDrawDowns = useMemo(() => applySearch(pendingDrawDowns), [applySearch, pendingDrawDowns]);
  const fReceipts = useMemo(() => applySearch(receipts), [applySearch, receipts]);
  const fPendingWht = useMemo(() => applySearch(pendingWht), [applySearch, pendingWht]);
  const fMatchedWht = useMemo(() => applySearch(matchedWht), [applySearch, matchedWht]);

  /* T3-114: the grouped, sorted, paginated views for the three paginated Matched tables.
   * Computed here (not inline at the render site) because each is needed TWICE — once for the rows
   * and once for TablePagination's total — and the two must be the same computation, or the pager
   * would count something other than what is on screen. */
  const matchedView = useMemo(
    () => groupPaginateSort(fMatched, matchedPaymentsSort, MATCHED_PAYMENT_COLUMNS, matchedPg),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fMatched, matchedPaymentsSort.sorts, matchedPg.page, matchedPg.rowsPerPage],
  );
  const receiptView = useMemo(
    () => groupPaginateSort(fReceipts, receiptsSort, RECEIPT_COLUMNS, receiptPg),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fReceipts, receiptsSort.sorts, receiptPg.page, receiptPg.rowsPerPage],
  );
  const matchedWhtView = useMemo(
    () => groupPaginateSort(fMatchedWht, matchedWhtSort, MATCHED_WHT_COLUMNS, matchedWhtPg),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fMatchedWht, matchedWhtSort.sorts, matchedWhtPg.page, matchedWhtPg.rowsPerPage],
  );
  const switchToPaymentTab = (key) => {
    const idx = visiblePaymentTabs.findIndex(t => (t.key || t.feature) === key);
    if (idx >= 0) setTabValue(idx);
  };

  // Deeplink support: /payments?tab=matching&contractRef=ABC pre-selects the tab
  // and pre-populates the contract search. One-shot per mount — once the dashboard
  // (or any other source) sends a user here, we honour the hint then get out of the way.
  const [searchParams] = useSearchParams();
  const deeplinkApplied = useRef(false);
  useEffect(() => {
    if (deeplinkApplied.current) return;
    if (!visiblePaymentTabs || visiblePaymentTabs.length === 0) return;
    const tab = searchParams.get('tab');
    const ref = searchParams.get('contractRef');
    if (tab) switchToPaymentTab(tab);
    if (ref) setMatchingSearch(ref);
    if (tab || ref) deeplinkApplied.current = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePaymentTabs, searchParams]);

  // Deeplink: /payments?scheduleId=<cas.id> auto-opens the match modal for that
  // schedule row (clicked from the dashboard Upcoming Events list). The payment
  // feeds load async, so we wait for them, then find the row across all feeds,
  // switch to its tab, and open the dialog. Tracking the last-applied id (rather
  // than a one-shot boolean) lets a fresh navigation with a different id re-fire.
  const deeplinkScheduleApplied = useRef(null);
  useEffect(() => {
    const scheduleId = searchParams.get('scheduleId');
    if (!scheduleId || deeplinkScheduleApplied.current === scheduleId) return;
    // Wait until the feeds have actually loaded before deciding "not found".
    // `loading` is unreliable here (starts false), so gate on dataLoaded.
    if (!dataLoaded) return;

    // Each group ties a top tab to its overdue/upcoming sub-feeds and the
    // sub-tab setter. Sub-tab index: 0 = Overdue, 1 = Upcoming. We set it so
    // the row is visible behind the modal (dashboard events are future-dated,
    // so they almost always land on the Upcoming sub-tab).
    // The Capital tab additionally has an In/Out direction toggle (default 'in').
    // Dashboard Capital events are outgoing, so the capital group also flips
    // capitalDirection to 'out' — without it the Out section isn't rendered.
    const groups = [
      { key: 'matching',         setSub: setPaymentsSubTab,   overdue: overduePayments,    upcoming: upcomingPayments },
      { key: 'combined',         setSub: setCombinedSubTab,   overdue: combinedOverdue,    upcoming: combinedUpcoming },
      { key: 'capital_receipts', setSub: setCapitalOutSubTab, overdue: capitalOutOverdue,  upcoming: capitalOutUpcoming,
        setDirection: setCapitalDirection, direction: 'out' },
    ];

    let found = null;
    for (const g of groups) {
      const idMatch = (r) => String(r.id) === String(scheduleId);
      if (g.overdue.some(idMatch)) {
        found = { ...g, subIndex: 0, row: g.overdue.find(idMatch) };
        break;
      }
      if (g.upcoming.some(idMatch)) {
        found = { ...g, subIndex: 1, row: g.upcoming.find(idMatch) };
        break;
      }
    }

    deeplinkScheduleApplied.current = scheduleId;
    if (found) {
      switchToPaymentTab(found.key);
      if (found.setDirection) found.setDirection(found.direction);
      found.setSub(found.subIndex);
      handleOpenMatchDialog(found.row);
    } else {
      notify.info('This payment is no longer pending — it may already be matched.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, dataLoaded, overduePayments, upcomingPayments, combinedOverdue, combinedUpcoming, capitalOutOverdue, capitalOutUpcoming]);

  if (isMasterTenant) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>Matching</Typography>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Matching is not available on the Master tenant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Switch to an operating tenant to reconcile payments.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Matching</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Horizon for the Upcoming queues (interest / combined / capital-out).
              Styled to match the Dashboard "Maturing Soon" toggle (All first, en-dash
              ranges, same size/padding), but the SEMANTICS differ deliberately: these
              are CUMULATIVE ("everything due within N days"), where the Dashboard's
              0-30 / 31-60 / 61-90 are DISJOINT buckets for a breakdown.
              Hence 0–30 / 0–60 / 0–90 and NOT 31–60 / 61–90 — do not "align" the
              labels further. This is a work queue: a disjoint 31–60 would HIDE the
              imminent rows, which is the exact bug this filter was added to fix.
              Applied server-side — see loadAllPaymentData. */}
          <Tooltip title="How far ahead the Upcoming queues look. Overdue rows are always shown in full, however old.">
            <ToggleButtonGroup
              value={paymentWindow}
              exclusive
              onChange={(event, next) => { if (next !== null) setPaymentWindow(next); }}
              size="small"
              disabled={loading}
              aria-label="Upcoming payments horizon"
            >
              <ToggleButton value="all" sx={{ px: 1, py: 0.25 }}>All</ToggleButton>
              <ToggleButton value="30" sx={{ px: 1, py: 0.25 }}>0–30</ToggleButton>
              <ToggleButton value="60" sx={{ px: 1, py: 0.25 }}>0–60</ToggleButton>
              <ToggleButton value="90" sx={{ px: 1, py: 0.25 }}>0–90</ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAllPaymentData}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      {/* Summary Cards — Approvals-style StatCards with per-direction breakdowns.
          Clicking a card switches the top tab. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="All Outflows"
            value={overduePayments.length + upcomingPayments.length + combinedOverdue.length + combinedUpcoming.length + capitalOutOverdue.length + capitalOutUpcoming.length + pendingWht.length}
            subtitle="All outstanding"
            Icon={AccountBalanceIcon}
            color="#1976d2"
            breakdown={[
              { label: 'Overdue', value: overduePayments.length + combinedOverdue.length + capitalOutOverdue.length },
              { label: 'Upcoming', value: upcomingPayments.length + combinedUpcoming.length + capitalOutUpcoming.length },
              { label: 'Withholding tax pending', value: pendingWht.length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Interest Payments"
            value={overduePayments.length + upcomingPayments.length}
            Icon={PaymentIcon}
            color="#0088FE"
            selected={paymentsTabKey === 'matching'}
            onClick={() => switchToPaymentTab('matching')}
            breakdown={[
              { label: 'Overdue', value: overduePayments.length },
              { label: 'Upcoming', value: upcomingPayments.length },
              { label: 'Matched', value: matchedPayments.filter(p => p.payment_type !== 'Full Settlement').length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Combined"
            value={combinedOverdue.length + combinedUpcoming.length}
            subtitle="Annuity"
            Icon={MergeIcon}
            color="#00C49F"
            selected={paymentsTabKey === 'combined'}
            onClick={() => switchToPaymentTab('combined')}
            breakdown={[
              { label: 'Overdue', value: combinedOverdue.length },
              { label: 'Upcoming', value: combinedUpcoming.length },
              { label: 'Matched', value: matchedPayments.filter(p => p.payment_type === 'Full Settlement').length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Capital"
            value={pendingDrawDowns.length + capitalOutOverdue.length + capitalOutUpcoming.length}
            subtitle="In + Out"
            Icon={SwapVertIcon}
            color="#FFBB28"
            selected={paymentsTabKey === 'capital_receipts'}
            onClick={() => switchToPaymentTab('capital_receipts')}
            breakdown={[
              { label: 'Incoming pending', value: pendingDrawDowns.length },
              { label: 'Outgoing overdue', value: capitalOutOverdue.length },
              { label: 'Outgoing upcoming', value: capitalOutUpcoming.length },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard
            title="Withholding Tax"
            value={pendingWht.length}
            subtitle="Tax remittances"
            Icon={ReceiptLongIcon}
            color="#FF8042"
            selected={paymentsTabKey === 'withholding_tax'}
            onClick={() => switchToPaymentTab('withholding_tax')}
            breakdown={[
              { label: 'Pending', value: pendingWht.length },
              { label: 'Matched', value: matchedWht.length },
            ]}
          />
        </Grid>
      </Grid>
      {/* Contract search — filters every visible row by contract ref / user ref / investor name. */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search by contract reference, external reference, or investor…"
          value={matchingSearch}
          onChange={(e) => setMatchingSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 480 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <span style={{ color: '#888' }}>⌕</span>
              </InputAdornment>
            ),
            endAdornment: matchingSearch ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setMatchingSearch('')} aria-label="Clear search">
                  <span style={{ fontSize: 16 }}>×</span>
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {matchingSearch && (
          <Typography variant="caption" color="textSecondary">
            Filtered
          </Typography>
        )}
      </Box>
      {/* Top-Level Tabs. When a search is active each tab gets a (N) suffix
          counting filtered matches under that tab, so the user can see at a
          glance which tabs have hits without having to click each one. */}
      <Paper sx={{ mb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={safeTabValue} onChange={(_, v) => setTabValue(v)}>
          {visiblePaymentTabs.map(t => {
            const key = t.key || t.feature;
            let count = 0;
            if (key === 'matching') count = fOverdue.length + fUpcoming.length + fMatched.filter(p => p.payment_type !== 'Full Settlement').length;
            else if (key === 'combined') count = fCombinedOverdue.length + fCombinedUpcoming.length + fMatched.filter(p => p.payment_type === 'Full Settlement').length;
            else if (key === 'capital_receipts') count = fPendingDrawDowns.length + fReceipts.length + fCapitalOutOverdue.length + fCapitalOutUpcoming.length;
            else if (key === 'withholding_tax') count = fPendingWht.length + fMatchedWht.length;
            const label = matchingSearch ? `${t.label} (${count})` : t.label;
            return <Tab key={key} label={label} />;
          })}
        </Tabs>
        {activePaymentFeature === 'capital_receipts' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 2 }}>
            <Typography variant="caption" color="textSecondary">Direction:</Typography>
            <Button
              size="small"
              variant={capitalDirection === 'in' ? 'contained' : 'outlined'}
              onClick={() => setCapitalDirection('in')}
            >
              Incoming ({fPendingDrawDowns.length + fReceipts.length})
            </Button>
            <Button
              size="small"
              variant={capitalDirection === 'out' ? 'contained' : 'outlined'}
              onClick={() => setCapitalDirection('out')}
            >
              Outgoing ({fCapitalOutOverdue.length + fCapitalOutUpcoming.length})
            </Button>
          </Box>
        )}
      </Paper>
      {/* ===== Payments Tab ===== */}
      {activePaymentFeature === 'matching' && (
      <Box>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={paymentsSubTab} onChange={(_, v) => setPaymentsSubTab(v)}>
            <Tab label={`Overdue (${fOverdue.length})`} />
            <Tab label={`Upcoming (${fUpcoming.length})`} />
            <Tab label={`Matched (${fMatched.length})`} />
          </Tabs>
        </Paper>

        {/* Overdue Payments Sub-Tab */}
        <TabPanel value={paymentsSubTab} index={0}>
          <OverduePaymentsTab
            payments={fOverdue.map(toTableRow)}
            showContractColumn={true}
            showInvestorColumn={true}
            showMatchActions={true}
            onMatch={handleOpenMatchDialog}
            paginated
            groupMultiInvestor
          />
        </TabPanel>

        {/* Upcoming Payments Sub-Tab.
            showFilter={false}: the page-level horizon toggle governs all three
            Upcoming queues at once, so this component's own dropdown (which would
            only filter the already-fetched set) stays suppressed. */}
        <TabPanel value={paymentsSubTab} index={1}>
          <UpcomingPaymentsTab
            payments={fUpcoming.map(toTableRow)}
            showContractColumn={true}
            showInvestorColumn={true}
            showMatchActions={true}
            onMatch={handleOpenMatchDialog}
            showFilter={false}
            paginated
            groupMultiInvestor
          />
        </TabPanel>

        {/* Matched Payments Sub-Tab */}
        <TabPanel value={paymentsSubTab} index={2}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="payment_date">Payment Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                    {/* T3-114/T3-115: the same accounting triple the unmatched queues show, as real
                        columns rather than inline text in the group header. Blank on payment rows —
                        an individual payment has no outstanding balance of its own; the LINE does. */}
                    <TableCell align="right">Carried in</TableCell>
                    <TableCell align="right">Allocated</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="matched_date">Matched Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="matched_by">Matched By</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="reference">Reference</SortableHeaderCell>
                    <TableCell>Proof</TableCell>
                    <SortableHeaderCell sort={matchedPaymentsSort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="right">Variance</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedView.groups.flatMap((group) => {
                    const expanded = isGroupExpanded(group);
                    // lead 3 = Contract/Investor/Payment Date; then Amount/Allocated/Outstanding;
                    // 4 filler = Matched Date/Matched By/Reference/Proof; Status; 2 = Variance/Actions.
                    const header = renderGroupHeader(group, { lead: 3, beforeStatus: 4, trailing: 2 }, expanded);
                    if (!expanded) return [header];
                    return [header, ...group.rows.map((payment) => (
                    <TableRow
                      key={payment.id}
                      hover
                      onClick={() => handleOpenViewDialog(payment, 'payment')}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{investorLabel(payment)}</Typography>
                      </TableCell>
                      <TableCell>
                        {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {payment.currency_code} {formatNumber(payment.payment_amount)}
                        </Typography>
                      </TableCell>
                      {/* Carried in / Allocated / Outstanding are LINE-level, stated once on the
                          group header above. Blank here deliberately: an individual payment has no
                          outstanding balance of its own, and repeating the line's figures on every
                          row that settled it would imply each payment carried them. */}
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell>
                        {payment.processed_at ? format(new Date(payment.processed_at), 'dd MMM yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {payment.processed_by || payment.created_by || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{payment.payment_reference || '-'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {/* T3-129: N documents. One doc renders exactly as before. */}
                        <PaymentProofCell payment={payment} docActions={docActions} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.payment_status}
                          size="small"
                          variant="outlined"
                          color={getStatusColor(payment.payment_status)}
                        />
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {payment.variance_amount != null && Math.abs(parseFloat(payment.variance_amount)) >= 0.01 ? (
                          /* T3-115: this figure is the variance struck AT THE TIME THIS PAYMENT WAS
                             MATCHED, and it can legitimately differ from the line's outstanding
                             balance on the header above — e.g. Med-SZ-2026-0002, where the interest
                             leg was recorded short by 1,376.72 and the tax leg of 668.84 landed
                             afterwards, leaving the line 707.88 short. Both numbers are true of
                             different moments. The stored variance is a historical record and must
                             NOT be recomputed; the tooltip says which is which so two figures on one
                             screen do not read as a contradiction. */
                          <Tooltip title={`Variance recorded when this payment was matched${payment.variance_reason ? `: ${payment.variance_reason}` : ' (no reason recorded)'} — the line's current balance is shown on the group row above.`}>
                            <Chip
                              size="small"
                              label={`${parseFloat(payment.variance_amount) < 0 ? 'Short' : 'Over'} ${payment.currency_code || ''} ${formatNumber(Math.abs(parseFloat(payment.variance_amount)))}`}
                              color={parseFloat(payment.variance_amount) < 0 ? 'error' : 'success'}
                              variant="outlined"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="textSecondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit apportionment / variance reason">
                          <IconButton size="small" color="primary" onClick={() => handleOpenReapportionDialog(payment)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Unmatch payment">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleUnmatchPayment(payment)}
                          >
                            <UnmatchIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))];
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {/* T3-114: count GROUPS, not payments — the table renders one line per group. */}
            <TablePagination {...matchedPg.tableProps(matchedView.count)} />
          </Paper>
        </TabPanel>
      </Box>
      )}
      {/* ===== Combined Payments Tab (annuity / combine_payment_rows contracts) ===== */}
      {activePaymentFeature === 'combined' && (
      <Box>
        {(() => {
          // Combined matches are sp_payment_match_inline rows where the row had
          // both interest and capital — sp_payment_match_inline assigns
          // payment_type = 'Full Settlement' in that case. Filter the existing
          // matched feed; no new endpoint needed.
          const combinedMatched = fMatched.filter(p => p.payment_type === 'Full Settlement');
          return (
            <>
              <Paper sx={{ mb: 0 }}>
                <Tabs value={combinedSubTab} onChange={(_, v) => setCombinedSubTab(v)}>
                  <Tab label={`Overdue (${fCombinedOverdue.length})`} />
                  <Tab label={`Upcoming (${fCombinedUpcoming.length})`} />
                  <Tab label={`Matched (${combinedMatched.length})`} />
                </Tabs>
              </Paper>

              <TabPanel value={combinedSubTab} index={0}>
                <OverduePaymentsTab
                  payments={fCombinedOverdue.map(toTableRow)}
                  showContractColumn={true}
                  showInvestorColumn={true}
                  showMatchActions={true}
                  onMatch={handleOpenMatchDialog}
                  paginated
                  groupMultiInvestor
                />
              </TabPanel>

              <TabPanel value={combinedSubTab} index={1}>
                <UpcomingPaymentsTab
                  payments={fCombinedUpcoming.map(toTableRow)}
                  showContractColumn={true}
                  showInvestorColumn={true}
                  showMatchActions={true}
                  onMatch={handleOpenMatchDialog}
                  showFilter={false}
                  paginated
                  groupMultiInvestor
                />
              </TabPanel>

              <TabPanel value={combinedSubTab} index={2}>
                <Paper>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell>Contract</TableCell>
                          <TableCell>Investor</TableCell>
                          <TableCell>Payment Date</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell align="right">Allocated Interest</TableCell>
                          <TableCell align="right">Allocated Capital</TableCell>
                          <TableCell>Matched Date</TableCell>
                          <TableCell>Matched By</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Proof</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Variance</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {combinedMatched.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={13} align="center">
                              <Typography variant="body2" color="textSecondary" sx={{ py: 3 }}>
                                No matched combined payments yet.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          groupByContract(combinedMatched).flatMap((group) => {
                          const expanded = isGroupExpanded(group);
                          const header = renderGroupHeader(group, 13, expanded);
                          if (!expanded) return [header];
                          return [header, ...group.rows.map((payment) => (
                            <TableRow
                              key={payment.id}
                              hover
                              onClick={() => handleOpenViewDialog(payment, 'combined')}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} />
                              </TableCell>
                              <TableCell><Typography variant="body2">{investorLabel(payment)}</Typography></TableCell>
                              <TableCell>{format(new Date(payment.payment_date), 'dd MMM yyyy')}</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight="medium">
                                  {payment.currency_code} {formatNumber(payment.payment_amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {payment.allocated_interest != null
                                  ? `${payment.currency_code} ${formatNumber(payment.allocated_interest)}`
                                  : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {payment.allocated_capital != null
                                  ? `${payment.currency_code} ${formatNumber(payment.allocated_capital)}`
                                  : '—'}
                              </TableCell>
                              <TableCell>{payment.processed_at ? format(new Date(payment.processed_at), 'dd MMM yyyy') : '-'}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                  {payment.processed_by || payment.created_by || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>{payment.payment_reference || '-'}</TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                {/* T3-129: N documents. One doc renders exactly as before. */}
                                <PaymentProofCell payment={payment} docActions={docActions} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={payment.payment_status || 'Matched'}
                                  size="small"
                                  variant="outlined"
                                  color={getStatusColor(payment.payment_status || 'matched')}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {payment.variance_amount != null && Math.abs(parseFloat(payment.variance_amount)) >= 0.01 ? (
                                  <Tooltip title={payment.variance_reason || 'No reason recorded'}>
                                    <Chip
                                      size="small"
                                      label={`${parseFloat(payment.variance_amount) < 0 ? 'Short' : 'Over'} ${payment.currency_code || ''} ${formatNumber(Math.abs(parseFloat(payment.variance_amount)))}`}
                                      color={parseFloat(payment.variance_amount) < 0 ? 'error' : 'success'}
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">—</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Edit apportionment / variance reason">
                                  <IconButton size="small" color="primary" onClick={() => handleOpenReapportionDialog(payment)}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Unmatch payment">
                                  <IconButton size="small" color="warning" onClick={() => handleUnmatchPayment(payment)}>
                                    <UnmatchIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))];
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </TabPanel>
            </>
          );
        })()}
      </Box>
      )}
      {/* ===== Capital Tab ===== */}
      {activePaymentFeature === 'capital_receipts' && (
      <Box>
        {/* Direction switch lives inline with the top-level tabs (right side
            of the Paper above). The body just renders the active direction. */}
        {capitalDirection === 'in' && (
        <>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={capitalSubTab} onChange={(_, v) => setCapitalSubTab(v)}>
            <Tab label={`Pending Receipts (${fPendingDrawDowns.length})`} />
            <Tab label={`Matched Receipts (${fReceipts.length})`} />
          </Tabs>
        </Paper>

        {/* Pending Capital Receipts Sub-Tab */}
        <TabPanel value={capitalSubTab} index={0}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="expected_date">Expected Date</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="capital_amount" align="right">Capital Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingDrawDownsSort} columnKey="days_overdue">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingDrawDowns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 3 }}>
                          <MatchIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                          <Typography color="success.main" fontWeight="medium">
                            All capital has been received
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            No pending capital draw downs for active contracts
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingDrawDownPg.paginateData(pendingDrawDownsSort.sortRows(fPendingDrawDowns, PENDING_DRAWDOWN_COLUMNS)).map((drawDown) => (
                      <TableRow
                        key={`${drawDown.contract_id}-${drawDown.schedule_id}`}
                        hover
                        onClick={() => handleReceiptDrawDown(drawDown)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={drawDown.contract_reference} userReference={drawDown.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{investorLabel(drawDown)}</Typography>
                          {drawDown.investor_email && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {drawDown.investor_email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {format(new Date(drawDown.expected_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {drawDown.currency_code} {formatNumber(drawDown.capital_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {drawDown.days_overdue > 0 ? (
                            <Chip
                              label={`${drawDown.days_overdue} days overdue`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleReceiptDrawDown(drawDown)}
                          >
                            Receipt
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {fPendingDrawDowns.length > pendingDrawDownPg.rowsPerPage && (
              <TablePagination {...pendingDrawDownPg.tableProps(fPendingDrawDowns.length)} />
            )}
          </Paper>
        </TabPanel>

        {/* Matched Capital Receipts Sub-Tab */}
        <TabPanel value={capitalSubTab} index={1}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={receiptsSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="payment_date">Payment Date</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="matched_date">Matched Date</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="matched_by">Matched By</SortableHeaderCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="reference">Reference</SortableHeaderCell>
                    <TableCell>Proof</TableCell>
                    <SortableHeaderCell sort={receiptsSort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receipts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        <Box sx={{ py: 3 }}>
                          <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography color="textSecondary" fontWeight="medium">
                            No receipts recorded yet
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Capital receipts will appear here once recorded
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    receiptView.groups.flatMap((group) => {
                    const expanded = isGroupExpanded(group);
                    const header = renderGroupHeader(group, 10, expanded);
                    if (!expanded) return [header];
                    return [header, ...group.rows.map((receipt) => (
                      <TableRow
                        key={receipt.id}
                        hover
                        onClick={() => handleOpenViewDialog(receipt, 'capital-receipt')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={receipt.contract_reference || '-'} userReference={receipt.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{investorLabel(receipt)}</Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(receipt.received_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {receipt.currency} {formatNumber(receipt.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {receipt.created_at ? format(new Date(receipt.created_at), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {receipt.created_by_name || receipt.created_by || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{receipt.reference || '-'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {receipt.proof_document_id ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Preview Document">
                                <IconButton
                                  size="small"
                                  onClick={() => docActions.openPreview(receipt.proof_document_id)}
                                >
                                  <PreviewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download Proof">
                                <IconButton
                                  size="small"
                                  onClick={() => docActions.downloadDocument(receipt.proof_document_id)}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Matched"
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Unmatch receipt">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleDeleteReceipt(receipt.id)}
                            >
                              <UnmatchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))];
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {fReceipts.length > receiptPg.rowsPerPage && (
              <TablePagination {...receiptPg.tableProps(receiptView.count)} />
            )}
          </Paper>
        </TabPanel>
        </>
        )}
        {capitalDirection === 'out' && (
        <>
          <Paper sx={{ mb: 0 }}>
            <Tabs value={capitalOutSubTab} onChange={(_, v) => setCapitalOutSubTab(v)}>
              <Tab label={`Overdue (${fCapitalOutOverdue.length})`} />
              <Tab label={`Upcoming (${fCapitalOutUpcoming.length})`} />
            </Tabs>
          </Paper>

          <TabPanel value={capitalOutSubTab} index={0}>
            <OverduePaymentsTab
              payments={fCapitalOutOverdue.map(toTableRow)}
              showContractColumn={true}
              showInvestorColumn={true}
              showMatchActions={true}
              onMatch={handleOpenMatchDialog}
              paginated
              groupMultiInvestor
            />
          </TabPanel>

          <TabPanel value={capitalOutSubTab} index={1}>
            <UpcomingPaymentsTab
              payments={fCapitalOutUpcoming.map(toTableRow)}
              showContractColumn={true}
              showInvestorColumn={true}
              showMatchActions={true}
              onMatch={handleOpenMatchDialog}
              showFilter={false}
              paginated
              groupMultiInvestor
            />
          </TabPanel>
        </>
        )}
      </Box>
      )}
      {/* ===== Withholding Tax Tab ===== */}
      {activePaymentFeature === 'withholding_tax' && (
      <Box>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={whtSubTab} onChange={(_, v) => setWhtSubTab(v)}>
            <Tab label={`Pending Withholding Tax (${fPendingWht.length})`} />
            <Tab label={`Matched Withholding Tax (${fMatchedWht.length})`} />
          </Tabs>
        </Paper>

        {/* Pending WHT Payments Sub-Tab */}
        <TabPanel value={whtSubTab} index={0}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="interest_date">Interest Date</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="gross_interest" align="right">Gross Interest</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="wht_rate" align="right">WHT Rate</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="wht_due" align="right">WHT Due</SortableHeaderCell>
                    <SortableHeaderCell sort={pendingWhtSort} columnKey="days_overdue">Status</SortableHeaderCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingWht.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box sx={{ py: 3 }}>
                          <MatchIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                          <Typography color="success.main" fontWeight="medium">
                            No pending withholding tax payments
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            All withholding tax payments are up to date
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingWhtPg.paginateData(pendingWhtSort.sortRows(fPendingWht, PENDING_WHT_COLUMNS)).map((entry) => (
                      <TableRow
                        key={entry.schedule_id}
                        hover
                        onClick={() => handleOpenWhtMatchDialog(entry)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <ContractReferenceCell reference={entry.contract_reference} userReference={entry.user_reference} />
                            {/* T3-059: matured contracts stay matchable for pending WHT (Path A). */}
                            {entry.contract_status === 'Matured' && (
                              <Chip label="Matured" size="small" color="secondary" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{investorLabel(entry)}</Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(entry.payment_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {entry.currency_code} {formatNumber(entry.gross_interest_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {parseFloat(entry.wht_rate_applied || 0).toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {entry.currency_code} {formatNumber(entry.wht_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {entry.days_overdue > 0 ? (
                            <Chip
                              label={`${entry.days_overdue} days overdue`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenWhtMatchDialog(entry)}
                          >
                            Match
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {fPendingWht.length > pendingWhtPg.rowsPerPage && (
              <TablePagination {...pendingWhtPg.tableProps(fPendingWht.length)} />
            )}
          </Paper>
        </TabPanel>

        {/* Matched WHT Payments Sub-Tab */}
        <TabPanel value={whtSubTab} index={1}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="contract">Contract</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="investor">Investor</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="payment_date">Payment Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="amount" align="right">Amount</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="matched_date">Matched Date</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="matched_by">Matched By</SortableHeaderCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="reference">Reference</SortableHeaderCell>
                    <TableCell>Proof</TableCell>
                    <SortableHeaderCell sort={matchedWhtSort} columnKey="status">Status</SortableHeaderCell>
                    <TableCell align="right">Variance</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedWht.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Box sx={{ py: 3 }}>
                          <ReceiptIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography color="textSecondary" fontWeight="medium">
                            No matched withholding tax payments yet
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matchedWhtView.groups.flatMap((group) => {
                    const expanded = isGroupExpanded(group);
                    const header = renderGroupHeader(group, 11, expanded);
                    if (!expanded) return [header];
                    return [header, ...group.rows.map((payment) => (
                      <TableRow
                        key={payment.id}
                        hover
                        onClick={() => handleOpenViewDialog(payment, 'wht')}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <ContractReferenceCell reference={payment.contract_reference} userReference={payment.user_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{investorLabel(payment)}</Typography>
                        </TableCell>
                        <TableCell>
                          {format(new Date(payment.payment_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {payment.currency_code} {formatNumber(payment.payment_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.processed_at ? format(new Date(payment.processed_at), 'dd MMM yyyy') : '-'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {payment.processed_by || payment.created_by || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{payment.payment_reference || '-'}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {/* T3-129: N documents. One doc renders exactly as before. */}
                          <PaymentProofCell payment={payment} docActions={docActions} />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.payment_status || 'Matched'}
                            size="small"
                            variant="outlined"
                            color={getStatusColor(payment.payment_status || 'matched')}
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {payment.variance_amount != null && Math.abs(parseFloat(payment.variance_amount)) >= 0.01 ? (
                            <Tooltip title={payment.variance_reason || 'No reason recorded'}>
                              <Chip
                                size="small"
                                label={`${parseFloat(payment.variance_amount) < 0 ? 'Short' : 'Over'} ${payment.currency_code || ''} ${formatNumber(Math.abs(parseFloat(payment.variance_amount)))}`}
                                color={parseFloat(payment.variance_amount) < 0 ? 'error' : 'success'}
                                variant="outlined"
                              />
                            </Tooltip>
                          ) : (
                            <Typography variant="caption" color="textSecondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Edit apportionment / variance reason">
                            <IconButton size="small" color="primary" onClick={() => handleOpenReapportionDialog(payment)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Unmatch payment">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleUnmatchWht(payment.id)}
                            >
                              <UnmatchIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))];
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {fMatchedWht.length > matchedWhtPg.rowsPerPage && (
              <TablePagination {...matchedWhtPg.tableProps(matchedWhtView.count)} />
            )}
          </Paper>
        </TabPanel>
      </Box>
      )}

      {/* ===================================================================================
          BROKER FEES (Feature 30 Phase 4)

          Mirrors the Withholding Tax block above: a sub-tab pair (Pending / Matched) over the
          two fee feeds. Deliberately simpler than the interest tabs — no group rows, no
          apportionment, no variance chip — because fee matching is all-or-nothing against a
          single amount.

          ⚠ Amounts shown are the VAT-INCLUSIVE obligation (scheduled_amount = amount_due). The
          fee schedule stores payment_amount VAT-EXCLUSIVE with vat_amount alongside, so showing
          payment_amount here would understate what the broker is actually owed.
          =================================================================================== */}
      {activePaymentFeature === 'broker_fees' && (
      <Box>
        <Paper sx={{ mb: 0 }}>
          <Tabs value={feeSubTab} onChange={(_, v) => setFeeSubTab(v)}>
            <Tab label={`Pending Broker Fees (${pendingFees.length})`} />
            <Tab label={`Matched Broker Fees (${matchedFees.length})`} />
          </Tabs>
        </Paper>

        <TabPanel value={feeSubTab} index={0}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Contract</TableCell>
                    <TableCell>Broker</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell align="right">Amount Due</TableCell>
                    <TableCell align="right">Matched</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingFees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                          No broker fee payments outstanding
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingFees.map((entry) => (
                      <TableRow key={entry.fee_schedule_id} hover>
                        <TableCell>
                          <ContractReferenceCell reference={entry.contract_reference} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entry.broker_name || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          {entry.payment_date ? format(new Date(entry.payment_date), 'dd MMM yyyy') : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {entry.currency_code} {formatNumber(entry.scheduled_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="textSecondary">
                            {formatNumber(entry.matched_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatNumber(entry.outstanding_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {/* days_overdue is clamped at 0 by the feed, so > 0 means genuinely late.
                              A part-paid row is flagged separately: it is still outstanding, but
                              for a different reason than being late. */}
                          {entry.days_overdue > 0 ? (
                            <Chip label={`${entry.days_overdue}d overdue`} size="small" color="error" variant="outlined" />
                          ) : parseFloat(entry.matched_amount) > 0 ? (
                            <Chip label="Part paid" size="small" color="warning" variant="outlined" />
                          ) : (
                            <Chip label="Pending" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenFeeMatchDialog(entry)}
                          >
                            Match
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>

        <TabPanel value={feeSubTab} index={1}>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>Contract</TableCell>
                    <TableCell>Paid On</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Processed By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {matchedFees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                          No matched broker fee payments
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    matchedFees.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>
                          <ContractReferenceCell reference={p.contract_reference} />
                        </TableCell>
                        <TableCell>
                          {p.payment_date ? format(new Date(p.payment_date), 'dd MMM yyyy') : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {p.currency_code} {formatNumber(p.payment_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{p.payment_reference || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">{p.processed_by || '—'}</Typography>
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          {/* ⚠ Unmatching RE-OPENS the schedule row to deletion by
                              sp_fee_schedule_clear, which refuses to remove a settled row. That
                              interaction has no analogue on the interest side. */}
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            onClick={() => handleUnmatchFee(p.id)}
                          >
                            Unmatch
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>
      </Box>
      )}

      {/* WHT Match Dialog — handled by unified MatchDialog (matchType='wht') */}
      {/* Match Payment Dialog — unified MatchDialog handles Payments / Combined / WHT */}
      {/* Record Receipt Dialog */}
      <Dialog open={receiptDialogOpen} onClose={handleCloseReceiptDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Record Capital Receipt</DialogTitle>
        <DialogContent>
          {/* Show contract details when receipting from pending draw down */}
          {selectedDrawDown ? (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.50' }}>
              <Typography variant="subtitle2" gutterBottom color="success.dark">
                Receipting Capital Draw Down
              </Typography>
              <Grid container spacing={1}>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Contract</Typography>
                  <ContractReferenceCell reference={selectedDrawDown.contract_reference} userReference={selectedDrawDown.user_reference} />
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Investor</Typography>
                  <Typography variant="body2">{investorLabel(selectedDrawDown)}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Expected Date</Typography>
                  <Typography variant="body2">
                    {format(new Date(selectedDrawDown.expected_date), 'dd MMM yyyy')}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="caption" color="textSecondary">Expected Capital</Typography>
                  <Typography variant="body2" fontWeight="medium" color="success.dark">
                    {selectedDrawDown.currency_code} {formatNumber(selectedDrawDown.capital_amount)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          ) : (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Manually record capital received from an investor
            </Typography>
          )}

          <form>
            <Grid container spacing={2}>
              {/* T117: foreign settlement row — only when the contract settles in another ccy. */}
              {receiptSettlementId && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={`Amount Received (${receiptSettlementCcy})`}
                      type="number"
                      fullWidth
                      required
                      value={receiptSettlementAmount}
                      onChange={(e) => setReceiptSettlementAmount(e.target.value)}
                      helperText={`Cash actually received in ${receiptSettlementCcy}`}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">{receiptSettlementCcy}</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      label={`FX Rate (${receiptSettlementCcy}/${selectedDrawDown?.currency_code || ''})`}
                      type="number"
                      fullWidth
                      required
                      value={receiptFxRate}
                      onChange={(e) => setReceiptFxRate(e.target.value)}
                      helperText={`Dealt rate; pre-filled from the contract's initial-capital rate, override with the actual rate`}
                    />
                  </Grid>
                </>
              )}
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                {receiptSettlementId ? (
                  <TextField
                    label={`Equivalent (${selectedDrawDown?.currency_code || ''})`}
                    fullWidth
                    disabled
                    value={(() => {
                      const s = parseFloat(receiptSettlementAmount) || 0;
                      const r = parseFloat(receiptFxRate) || 0;
                      return s > 0 && r > 0 ? formatNumber(s * r) : '';
                    })()}
                    helperText={(() => {
                      const s = parseFloat(receiptSettlementAmount) || 0;
                      const r = parseFloat(receiptFxRate) || 0;
                      return s > 0 && r > 0
                        ? `≈ ${selectedDrawDown?.currency_code} at ${receiptSettlementCcy}/${selectedDrawDown?.currency_code} ${formatNumber(r, 6)}`
                        : `Contract-currency equivalent (settlement × rate)`;
                    })()}
                    InputProps={{
                      startAdornment: selectedDrawDown ? (
                        <InputAdornment position="start">{selectedDrawDown.currency_code}</InputAdornment>
                      ) : null,
                    }}
                  />
                ) : (
                  <Controller
                    name="amount"
                    control={receiptControl}
                    rules={{ required: 'Amount is required' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Amount Received"
                        type="number"
                        fullWidth
                        required
                        InputProps={{
                          startAdornment: selectedDrawDown ? (
                            <InputAdornment position="start">
                              {selectedDrawDown.currency_code}
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    )}
                  />
                )}
              </Grid>

              {/* T119: display-only variance indicator (parity with the match modal). The
                  equivalent (received × rate) vs the expected capital, in contract currency.
                  Not persisted — capital receipts have no variance row. */}
              {receiptSettlementId && (
                <Grid size={{ xs: 12 }}>
                  {(() => {
                    const cap = parseFloat(selectedDrawDown?.capital_amount) || 0;
                    const s = parseFloat(receiptSettlementAmount) || 0;
                    const r = parseFloat(receiptFxRate) || 0;
                    const equiv = s > 0 && r > 0 ? +(s * r).toFixed(2) : 0;
                    const variance = equiv - cap;
                    const has = cap > 0 && equiv > 0 && Math.abs(variance) >= 0.01;
                    const ccy = selectedDrawDown?.currency_code || '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">Variance</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={!has ? 'default' : (variance < 0 ? 'error' : 'success')}
                          label={
                            !has
                              ? 'On schedule'
                              : `${variance < 0 ? 'Short' : 'Over'} ${ccy} ${formatNumber(Math.abs(variance))}`
                          }
                        />
                      </Box>
                    );
                  })()}
                </Grid>
              )}

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="received_date"
                  control={receiptControl}
                  rules={{ required: 'Date is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Date Received"
                      type="date"
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <Controller
                  name="reference"
                  control={receiptControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Payment Reference"
                      fullWidth
                      placeholder="Bank reference"
                    />
                  )}
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Controller
                    name="payment_method"
                    control={receiptControl}
                    render={({ field }) => (
                      <Select {...field} label="Payment Method">
                        <MenuItem value="EFT">EFT</MenuItem>
                        <MenuItem value="Wire Transfer">Wire Transfer</MenuItem>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={12}>
                <Controller
                  name="notes"
                  control={receiptControl}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Notes"
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="Additional notes"
                    />
                  )}
                />
              </Grid>

              {/* Proof of Receipt Upload — label switches between Required and Optional */}
              <Grid size={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Proof of Receipt {isWorkflowDocumentRequired('capital_receipt') ? '(Required)' : '(Optional)'}
                </Typography>
                <FileDropZone
                  file={receiptFile}
                  onFile={(f) => setReceiptFile(f)}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  promptText="Click to upload proof of receipt (bank statement, etc.), or drag and drop"
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReceiptDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReceiptSubmit(handleCreateReceipt)}
            startIcon={uploadingReceipt ? <CircularProgress size={20} /> : <ReceiptIcon />}
            disabled={uploadingReceipt || (isWorkflowDocumentRequired('capital_receipt') && !receiptFile)}
          >
            Record Receipt
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
      {/* Document Preview Dialog */}
      <DocumentPreviewDialog {...docActions} />
      <MatchDialog
        open={matchDialogOpenUnified}
        onClose={handleCloseUnifiedMatch}
        onSubmit={handleUnifiedMatchSubmit}
        submitting={matchDialogSubmitting}
        matchType={matchDialogType}
        scheduleRow={matchDialogRow}
        currencyCode={matchDialogRow?.currency || matchDialogRow?.currency_code || ''}
        settlementCurrencyCode={matchDialogSettlementCcy}
        contractReference={matchDialogRow?.contract_reference}
        userReference={matchDialogRow?.user_reference}
        investorName={matchDialogRow ? investorLabel(matchDialogRow) : undefined}
        whtContext={matchDialogRow?._wht_context}
        mode={matchDialogMode}
        initialPayment={matchDialogInitialPayment}
        initialAllocation={matchDialogInitialAllocation}
        initialVarianceReason={matchDialogInitialReason}
      />
    </Box>
  );
}

export default PaymentMatching;
